import type { TicketPriority } from '@/types';

/**
 * Sprint Backlog · Homework tasks (S01-S06).
 * Recovery 2026-05-02 (migration-foreman · Phase C).
 * Source: BACKLOG_TASKS arrays in 01_BUILD/sprint_0[1-6].html · 20 HW total.
 *
 * Distribution: S01:2 · S02:4 · S03:4 · S04:4 · S05:4 · S06:2 (matches plan).
 * Each HW has rich `prompt` field with task text + acceptance criteria inline (multi-paragraph).
 * Render path: Sprint Backlog tab in Sprint.tsx (Phase D · UI integration).
 */

export interface Homework {
  id: string;
  sprintId: string;
  code: string;
  title: string;
  icon: string;
  estimateMin: number;
  priority: TicketPriority;
  description: string;
  /** Full task text + inline acceptance criteria. May contain HTML (e.g. <strong>). */
  prompt: string;
}

export const HOMEWORK: ReadonlyArray<Homework> = [
  {
    id: 'hw_s01_01',
    sprintId: 'S01',
    code: 'HW-S01-01',
    title: 'Расскажи, кто ты и зачем сюда пришёл',
    icon: '👋',
    estimateMin: 10,
    priority: 'P3',
    description: 'Это задание-журнал, не «домашка». Просто текст в свободной форме, для себя. Цель — зафиксировать стартовую точку, чтобы через 17 спринтов было с чем сравнить.',
    prompt: `В 3-5 предложениях ответь:
— Кто ты сейчас (профессия, бэкграунд)?
— Почему пошёл(шла) в QA?
— Чего больше всего боишься в этом пути?
— Какой результат через 17 спринтов будет «успехом»?`,
  },
  {
    id: 'hw_s01_02',
    sprintId: 'S01',
    code: 'HW-S01-02',
    title: 'SDLC одного твоего любимого приложения',
    icon: '📱',
    estimateMin: 20,
    priority: 'P2',
    description: 'Применяем теорию SDLC к реальному продукту. Выбери приложение, которым ты пользуешься каждый день (Spotify, Telegram, Notion, любое).',
    prompt: `В 1 параграфе опиши:
— Какая SDLC-модель там работает (Waterfall / Agile / Scrum / Kanban)? По каким признакам ты это понимаешь (частота релизов, новые фичи, hotfix culture)?
— Какая роль QA в этой команде (предположение)? Видны ли тебе как пользователю их «следы» — скорость bug-fixes, качество новых фич?
— Если бы ты пришёл туда junior-QA, на какую церемонию ты бы попросил пускать в первый месяц?`,
  },
  {
    id: 'hw_s02_01',
    sprintId: 'S02',
    code: 'HW-S02-01',
    title: 'Перепиши плохое требование в INVEST-Story',
    icon: '📝',
    estimateMin: 25,
    priority: 'P1',
    description: 'Плохо сформулированное требование — самый частый источник переделок и багов. Учимся видеть, что не так, и переписывать в INVEST-формате.',
    prompt: `Плохо сформулированные требования — главная причина переделок и багов в спринте. Если ты научишься видеть, что не так, и переписывать в INVEST-формате, ты сэкономишь команде дни работы в каждом релизе.

<strong>Что сделать:</strong>
Дано требование от условного PM:
«Система должна быстро обрабатывать заказы и быть удобной для пользователей».

Это плохое требование. Найди в нём минимум 4 проблемы (двусмысленность,
отсутствие измеримости, скрытые non-functional требования, отсутствие
acceptance critique). Назови каждую конкретным термином из T01.

Затем перепиши его в 2-3 User Story в формате
«As a [роль], I want to [действие], so that [бизнес-цель]» так, чтобы
каждая Story проходила правило INVEST из T02.
Допустим, контекст — e-commerce каталог Wildberries-уровня.

Формат вывода: markdown-документ, две секции — «Что не так» (буллетный
список с пометкой типа проблемы) и «Перепиши» (3 Story + для каждой
проверка по 6 буквам INVEST буллетным чеклистом).

<strong>Acceptance:</strong>
• ≥4 проблемы найдено в исходном требовании, каждая с явным указанием типа («двусмысленность 'быстро'», «отсутствует измеримость», «смешано functional + non-functional» и т.п.).
• ≥2 User Story переписаны в формате As/I want/So that — без отступлений от шаблона.
• Для каждой Story явно проверены все 6 букв INVEST (I/N/V/E/S/T) — галочка + 1 фраза-обоснование на каждую букву.
• Хотя бы одна Story содержит измеримый критерий (число, время, %).

<strong>Подсказки:</strong>
- «Быстро» — двусмысленность: 200ms? 2s? за смену? Перепиши как
  «p95-latency обработки заказа ≤ 1.5s при 50 RPS» — измеримое
  non-functional требование.
- «Удобно для пользователей» — это смешение бизнес-цели и UX-критерия
  без AC. Декомпозируй: какая конкретно фича делает «удобно»?
  Поиск? Корзина? Checkout? — каждая своя Story.
- INVEST-T (Testable) — самая частая проблема у новичков. Если не
  придумывается тест — Story слишком абстрактна, дроби.

Anti-pattern: написать «As a user, I want to use the system, so that
I can use it» — формат соблюдён, INVEST провален. Story должна быть
про КОНКРЕТНОЕ ценное действие конкретной роли.

<strong>Self-check вопросы:</strong>
• Какая из твоих 2-3 Story НЕ Independent (зависит от другой)? Что можно сделать?
• Если PM ответит «нам нужно вчера» на Estimate-вопрос — что ты предложишь как junior-QA, чтобы не сорвать Estimate INVEST?`,
  },
  {
    id: 'hw_s02_02',
    sprintId: 'S02',
    code: 'HW-S02-02',
    title: 'Acceptance Criteria в формате Given / When / Then',
    icon: '✅',
    estimateMin: 30,
    priority: 'P1',
    description: 'AC в формате Given / When / Then — единый язык, на котором PM, разработчики и QA говорят о «что считается готовым». Тренируем.',
    prompt: `AC в формате Given / When / Then — это «один язык», на котором PM, разработчик и QA одинаково понимают, что считается готовым. После 6 AC ты будешь автоматически думать в этом формате, и это сильно ускорит разбор задач на груминге.

<strong>Что сделать:</strong>
Дана User Story (для условного интернет-магазина типа Wildberries или
Lamoda):
«As a registered customer, I want to leave a review on a product, so that
I can help other buyers make a better choice.»

Напиши **минимум 6 Acceptance Criteria** в формате Given / When / Then
(см. T03). Покрытие должно включать минимум по одному:
— Happy path (всё хорошо)
— Negative (что-то пошло не так)
— Edge case / Boundary (граничное значение или редкая ситуация)
— Security / Validation (защита от вредных данных)

Для каждого AC укажи тип в скобках после номера: AC-1 (Happy), AC-2
(Negative) и т.д.

Формат вывода — markdown-таблица с колонками: # · AC (G/W/T) · Тип ·
«Что станет тестом» (1 строка про тест-кейс из этого AC).

<strong>Acceptance:</strong>
• ≥6 AC в формате Given... When... Then... — все три ключевых слова присутствуют в каждом AC.
• Покрытие 4 типов: ≥1 happy, ≥1 negative, ≥1 edge case, ≥1 security/validation.
• В колонке «Что станет тестом» каждое AC порождает 1-2 предложения про конкретный тест-кейс — это упражнение на связку AC ↔ TC.
• Один из AC явно использует boundary (граничное число — длина текста, рейтинг 0/6, и т.п.).

<strong>Подсказки:</strong>
- Если AC выглядят однообразно («Given пользователь залогинен, When
  делает X, Then получает Y» × 6 раз) — у тебя плохое покрытие.
  Должны быть разные роли (залогиненный / нет), разные данные
  (валидные / невалидные / boundary), разные результаты (success / error / warning).
- Security-AC обычно про экранирование HTML/script — пример из
  «Практики на занятии» (упр. 5 в QA_Textbook): отзыв с тегами,
  XSS не выполняется. Это полностью валидный AC формата G/W/T.
- Edge case часто путают с negative. Negative — «делает что-то неправильное
  и получает ошибку». Edge — «делает правильное действие в редкой
  ситуации» (повторно оставляет отзыв; рейтинг ровно 0).

<strong>Self-check вопросы:</strong>
• Какой AC самый «опасный» — если этот сценарий не протестировать перед релизом, какой будет worst-case impact?
• Какой AC хорошо превращается в автотест, а какой — нет (только manual)? Почему?`,
  },
  {
    id: 'hw_s02_03',
    sprintId: 'S02',
    code: 'HW-S02-03',
    title: 'MoSCoW + карта рисков для бэклога спринта',
    icon: '📊',
    estimateMin: 20,
    priority: 'P2',
    description: 'Приоритизация бэклога — это про «что НЕ делать в этом спринте». Раскладываем 8 фич по MoSCoW и обосновываем каждую.',
    prompt: `Главная сложность приоритизации — не «что важное», а «что не делать в этом спринте». MoSCoW даёт чёткий язык для разговора с PM, когда команда упирается в реальность 4 человек на 2 недели.

<strong>Что сделать:</strong>
Дан бэклог из 8 User Stories гипотетического банковского мобильного
приложения (допустим, в стиле Sber или Тинькофф). Команда: 4 человека,
спринт 2 недели.

Распредели по MoSCoW-категориям (Must / Should / Could / Won't) — см. T04.
Для каждой Story дай 1 строку обоснования, почему именно эта категория.

Бэклог:
1. Двухфакторная аутентификация по SMS при входе (новая)
2. Перевод другому клиенту банка по номеру телефона
3. История операций за последние 12 месяцев (раньше было 3 месяца)
4. Темная тема приложения
5. Интеграция с фитнес-браслетом для оплаты с запястья
6. Push-нотификация о подозрительной операции (anti-fraud)
7. Кнопка «Поделиться чеком в WhatsApp»
8. Перевод по номеру карты в другой банк (СБП-аналог)

Формат вывода — markdown-таблица: # · Story · MoSCoW · Обоснование (1
строка) · Кто получит больше всего боли, если эту Story НЕ закрыть в
спринте?

<strong>Acceptance:</strong>
• Все 8 Story расклассифицированы Must/Should/Could/Won't (без 'не уверен').
• Распределение НЕ равномерное (не по 2 в каждую) — реальный спринт почти никогда не имеет 25/25/25/25; форсировать симметрию = плохая приоритизация.
• Колонка «Кто получит больше всего боли» заполнена для каждой Story — конкретно («существующие клиенты», «anti-fraud команда», «маркетинг», «никто значимый»).
• ≥1 Story в категории Won't (this sprint). Если все попали в M/S/C — вы недоиспользовали категорию W, у которой важнейшая роль: «явно отказались, не забыли».

<strong>Подсказки:</strong>
- Story #6 (anti-fraud notification) — почти наверняка Must: если у клиента
  украдут деньги, репутация банка летит. Это compliance-territory.
- Story #7 (поделиться чеком в WhatsApp) — Could или Won't. Это «приятная
  мелочь», не блокирует основной flow. Маркетинг хочет, но не критично.
- Story #5 (фитнес-браслет) — почти наверняка Won't this sprint: специфичная
  интеграция, требует hardware-партнёрства, большая. Но это НЕ значит
  «никогда»; это значит «не сейчас».
- Если у тебя 6 Must — ты переоценил(а) обязательность. Команда из
  4 человек на 2 недели физически не закроет 6 крупных Must.

<strong>Self-check вопросы:</strong>
• Какая твоя классификация изменилась бы, если бы PM сказал: «у нас через 2 недели — обязательный аудит регулятора по anti-fraud»?
• Что в MoSCoW-классификации страдает больше всего, когда команда — junior-only, без senior'ов?`,
  },
  {
    id: 'hw_s02_04',
    sprintId: 'S02',
    code: 'HW-S02-04',
    title: 'Static Testing · ревью требования',
    icon: '🔍',
    estimateMin: 25,
    priority: 'P2',
    description: 'Тестирование начинается до запуска кода — на ревью требований. Учимся находить пробелы, задавать вопросы и фиксировать риски.',
    prompt: `Тестирование начинается ещё до того, как код написан — на ревью требований. Если научиться видеть пробелы здесь, ты найдёшь баги, которые иначе всплыли бы через 2 спринта в production.

<strong>Что сделать:</strong>
Тебе пришло на ревью требование от PM (допустим, ты QA в e-commerce):

«Авторизованный пользователь должен иметь возможность сохранять товары
в избранное. При нажатии на иконку сердечка товар добавляется в список.
Список доступен из профиля. Пользователь может удалить товар из списка.
Список синхронизируется между устройствами.»

Проведи **Static Testing** этого требования (см. T07 — review без запуска
кода). Найди:
— Минимум 5 пробелов / двусмысленностей в требовании.
— Минимум 5 уточняющих вопросов к PM (которые задал бы junior-QA на
  review-сессии).
— Минимум 3 риска, которые requirement не покрывает (ситуация, в которой
  требование не описывает поведение).

Формат вывода — markdown-документ из 3 секций. Каждая находка пронумерована
и помечена severity (низкая / средняя / высокая) — см. T07 классификация
defects из ISTQB Ch.3.

<strong>Acceptance:</strong>
• ≥5 двусмысленностей / пробелов, каждая с явным цитированием куска требования и пояснением, почему это пробел.
• ≥5 вопросов к PM в формате законченных вопросов (не «уточнить про X», а «Сохраняется ли избранное при logout анонимного пользователя?»).
• ≥3 риска (НЕ ошибки в формулировке, а сценарии, которые требование не покрывает): что с товаром, когда он удалён из каталога? что с лимитом списка? что с приватностью?
• Каждой находке присвоен severity (low/medium/high) — это тренировка оценки, не «всё high».

<strong>Подсказки:</strong>
- «Сохраняет в избранное» — что именно сохраняется: ID товара? snapshot
  с ценой на момент? Если завтра цена изменится — что увидит пользователь?
  Это classic-пробел для review.
- «Доступен из профиля» — где конкретно? Раздел? Тапка в меню?
- «Синхронизируется между устройствами» — как часто? Real-time?
  При следующем логине? Какой механизм при offline-добавлении?
- На review (см. ISTQB Ch.3) роли: автор, модератор, рецензент, scribe.
  Ты сейчас в роли рецензента — твой вывод должен быть структурирован,
  не «вот эти места вызвали вопросы».

Не путай findings с предложениями. На static review ты НЕ переписываешь
требование — ты документируешь дефекты документа. Финальную правку
делает автор.

<strong>Self-check вопросы:</strong>
• Какой из твоих 5 вопросов уйдёт прямо в AC (Given/When/Then) после ответа PM?
• Какой из 3 рисков, если не закрыть, превратится в production-баг через 2-3 спринта?`,
  },
  {
    id: 'hw_s03_01',
    sprintId: 'S03',
    code: 'HW-S03-01',
    title: 'Карта типов тестирования (mind map)',
    icon: '🗺',
    estimateMin: 45,
    priority: 'P1',
    description: 'Карта типов тестирования — твой визуальный навигатор по предметной области. На собесе junior’у часто рисуют пустую коробочку «Test Types» и просят разложить.',
    prompt: `На собесе junior’у часто рисуют пустую коробочку «Test Types» и просят разложить. Эта карта — твой визуальный навигатор по предметной области, к которой ты будешь возвращаться весь следующий год работы.

<strong>Что сделать:</strong>
Создай mind map (Miro / Figma / FigJam / бумага + фото) со ВСЕМИ типами
тестирования из Sprint 03. Корневой узел — «Test Types». Три главные ветки:
Functional, Non-functional, Change-related. Под Non-functional разверни
8 категорий ISO/IEC 25010 (Performance Efficiency, Compatibility, Usability,
Reliability, Security, Maintainability, Portability, Functional Suitability).
Для каждого узла — 1 пример из реального приложения (можно гипотетического:
e-commerce / банк / SaaS / mobile). Risk-Based — отдельная плашка в углу
карты (это подход, не тип).

<strong>Acceptance:</strong>
• ≥10 типов и категорий покрыто: ≥6 ISO 25010 категорий в Non-functional ветке + Functional как отдельная ветка с примерами + Change-related (Confirmation / Regression / Maintenance) с примерами
• Каждая ветка имеет ≥1 пример (приложения или конкретного теста)
• Risk-Based Testing вынесен отдельно как подход, не как тип
• Photo / link на карту приложен в журнал тестировщика

<strong>Подсказки:</strong>
- Functional ветка крупнее Non-functional? Нормально, мы их потом проверим
  в курсе подробнее.
- Не путай уровни (Component/System/Acceptance) с типами — уровни не
  кладём в эту карту, у них своя ось (см. T01).
- Подходы (Black/White/Gray-box) — тоже отдельная ось, но для интереса
  можешь добавить «как смотрим» как mini-плашку у Functional/Non-functional.

<strong>Self-check вопросы:</strong>
• Где у тебя на карте Usability и Accessibility — в functional или non-functional? Почему?
• Какой тип тестирования подходит больше всего конкретно для мобильного банка? Расставь top-3.
• Какой тип ты бы добавил(а), если бы карта была про IoT-устройства (умная колонка)?`,
  },
  {
    id: 'hw_s03_02',
    sprintId: 'S03',
    code: 'HW-S03-02',
    title: 'Risk-Based приоритизация бэклога из 8 фич',
    icon: '⚖️',
    estimateMin: 30,
    priority: 'P2',
    description: 'Risk-Based Testing — это «не успеть всё, но успеть главное». Раскладываем 8 фич по Likelihood × Impact и обосновываем цифрами.',
    prompt: `Никто никогда не успевает протестировать всё. Risk-Based Testing — это инструмент, который превращает «не успеть всё» в «успеть главное». После этого упражнения ты будешь обосновывать приоритеты цифрами, не интуицией.

<strong>Что сделать:</strong>
Дан бэклог гипотетического e-commerce-проекта на следующий спринт (8 фич).
Расставь приоритеты по матрице Risk-Based Testing: Likelihood (1-5) × Impact (1-5),
классифицируй CRITICAL / IMPORTANT / MODERATE / LOW. Предложи стратегию
тестирования для каждой (full coverage / standard / smoke / skip).
Бэклог:
1. Apple Pay интеграция (новый платёжный метод)
2. Refactoring authentication — переход на OAuth 2.0 PKCE
3. Email-confirmation на изменение пароля (новый шаг)
4. Performance-improvement главной — lazy load изображений
5. Wishlist persistence — сохранение при logout
6. Reviews sorting — добавление сортировки отзывов по дате
7. FAQ — добавили 5 новых вопросов
8. Footer link update — поменяли URL «политика конфиденциальности»
Формат вывода — таблица (Google Sheets / Markdown). 4 колонки: Фича · Likelihood (1-5) · Impact (1-5) · Категория · Стратегия.

<strong>Acceptance:</strong>
• Все 8 фич классифицированы с явным указанием Likelihood (1-5) и Impact (1-5)
• Для каждой фичи — 1-2 строки обоснования: что повышает Likelihood (новизна? сложность? частота изменений?) и что повышает Impact (revenue? число юзеров? compliance?)
• Распределение по 4 категориям может быть skewed — реальные спринты редко имеют равномерное покрытие, форсировать «по 2 каждой» не нужно
• Стратегия для CRITICAL фич включает минимум 3 типа теста (functional + non-functional + ещё один)

<strong>Подсказки:</strong>
- Apple Pay — новая интеграция, касается денег → high likelihood + high impact.
- FAQ — статичный контент → low likelihood + low impact, smoke достаточно.
- Refactoring auth — лезет в shared-код, который трогает всех юзеров →
  impact высокий, даже если likelihood средний.
- Если в твоей классификации все 8 — CRITICAL/IMPORTANT, ты переоценил
  риски. Если все 8 — MODERATE/LOW — недооценил.

<strong>Self-check вопросы:</strong>
• Какая фича из 8 имеет самый высокий project risk (а не product risk)? Почему?
• На какую фичу ты потратил(а) бы больше всего часов QA? На какую — меньше всего?
• Если у тебя есть 3 дня на спринт, в каком порядке закроешь фичи?`,
  },
  {
    id: 'hw_s03_03',
    sprintId: 'S03',
    code: 'HW-S03-03',
    title: 'Exploratory Charter · расширенная сессия на demoblaze',
    icon: '🧭',
    estimateMin: 30,
    priority: 'P1',
    description: 'Exploratory testing без charter’а — это случайные клики. С charter’ом — структурированный поиск багов, до которых автотесты не дойдут.',
    prompt: `Exploratory testing без charter’а превращается в случайные клики за полчаса. С charter’ом — это структурированная сессия, которая находит баги, до которых автотесты никогда не дойдут.

<strong>Что сделать:</strong>
Расширение Mission из T09. В тикете-Mission ты сделал(а) одну сессию
на одной странице (60 мин, 5+ наблюдений). Здесь — другая сессия
на нескольких страницах с явным charter-структурированием.
Шаги:
1. Сформулируй Charter в формате SBTM:
   - Mission (1 предложение)
   - Areas (минимум 3 связанных страницы demoblaze, например: Catalog → Product page → Cart)
   - Duration (45-60 мин)
   - Focus (например: cross-page state — что сохраняется при переходах)
   - Out of scope (что не трогаешь)
2. Проведи сессию по charter. Засеки таймер.
3. Найди ≥3 наблюдения, которые проявляются ТОЛЬКО при кросс-страничном
   взаимодействии (т.е. не были бы найдены, если бы тестировал(а)
   каждую страницу отдельно).
4. Session Report: что покрыл(а), что нашёл(ла), что оставил(а) вне scope.

<strong>Acceptance:</strong>
• Charter записан до старта сессии (5+ строк)
• Минимум 3 страницы охвачены в одной сессии
• ≥3 cross-page наблюдения (могут быть как баги, так и confirmed-OK поведения, или открытые вопросы для PM — не нужно «придумывать» баги, если их нет)
• Применена минимум 1 эвристика SFDIPOT (Time, State или Operations)
• Session Report формализован: Coverage / Bugs / Open Questions / Time breakdown

<strong>Подсказки:</strong>
- Кросс-страничные баги: что в URL? localStorage сохраняется при logout/login?
  Корзина анонимного юзера переносится при регистрации? Фильтры каталога
  сохраняются при возврате со страницы товара?
- Сессия 45-60 мин ≠ 6 часов. Если найдёшь много — фиксируй и продолжай
  по charter, не сворачивай в боковую дорогу.
- SFDIPOT-State и SFDIPOT-Time особенно полезны для cross-page —
  «что произойдёт, если состояние изменится за время перехода?».

<strong>Self-check вопросы:</strong>
• Какое из 3+ наблюдений ты бы НЕ нашёл(ла), если бы тестировал(а) одну страницу?
• Какой Bug/Session Ratio получился у тебя в этой сессии?
• Что ты бы поменял(а) в Charter после сессии, если бы прогнал(а) её снова?`,
  },
  {
    id: 'hw_s03_04',
    sprintId: 'S03',
    code: 'HW-S03-04',
    title: 'Сравни два non-functional типа тестирования',
    icon: '🔬',
    estimateMin: 45,
    priority: 'P3',
    description: 'Non-functional типы — мост от «работает» к «работает быстро / безопасно / удобно». Большинство junior’ов не выходит за пределы functional.',
    prompt: `Большинство junior’ов застревают на functional testing и не идут дальше. Это упражнение — мост от «работает» к «работает быстро / безопасно / удобно». После него ты сможешь обсудить с senior’ом, какой non-functional тип критичен для следующего релиза.

<strong>Что сделать:</strong>
Выбери 2 типа non-functional testing из ISO/IEC 25010 (например, Performance
vs Security ИЛИ Usability vs Accessibility). Сделай сравнительный документ
по 4 осям:
1. Что именно проверяем (входные данные, ожидаемое поведение)
2. Какими инструментами (минимум 2 на каждый тип)
3. Критерий «прошёл/провалил» (конкретные метрики)
4. Когда в SDLC обычно делаем (на каком уровне, с какой частотой)
Формат — таблица или 2-страничный markdown-документ с заголовками.

<strong>Acceptance:</strong>
• Чётко выбраны 2 типа non-functional (не functional + non-functional)
• Для каждого типа названы ≥2 конкретных инструмента
• Pass/fail критерий измеримый (цифра или булева проверка), не «нормально работает»
• Указан уровень тестирования (System / System Integration / Acceptance) для каждого типа

<strong>Подсказки:</strong>
- Performance vs Load — это два <em>под</em>типа Performance Efficiency,
  не разные ISO 25010 категории. Лучше Performance vs Security (разные
  категории).
- Usability vs Accessibility — пересекаются, но разные стандарты (Nielsen
  heuristics vs WCAG 2.1) и разные инструменты (UserTesting vs axe DevTools).
- Если затрудняешься с инструментами — поищи в курсе «Sprint 12»
  (Performance + Security) для интуиции, что там детально разберём.

<strong>Self-check вопросы:</strong>
• Какой из выбранных типов сложнее автоматизировать и почему?
• Какой junior с большей вероятностью пропустит на собесе и в реальной работе?`,
  },
  {
    id: 'hw_s04_01',
    sprintId: 'S04',
    code: 'HW-S04-01',
    title: '10 Test Cases для функции поиска',
    icon: '🔬',
    estimateMin: 45,
    priority: 'P1',
    description: 'Шаблон Test Case в формате IEEE 829 — то, на чём тебя оценят на pre-screen задании любого собеса. Тренируем на функции поиска.',
    prompt: `Шаблон Test Case в формате IEEE 829 — это то, на чём тебя в первую очередь оценят на pre-screen задании любого собеса. После 10 кейсов на одной функции у тебя будет автоматический навык писать их без подглядывания в шаблон.

<strong>Что сделать:</strong>
Напиши **10 детальных Test Cases** в формате IEEE 829 для функции поиска
на сайте \`saucedemo.com\` (или \`automationexercise.com\` если функция поиска
на saucedemo не подходит — выбирай сам).

Покрытие должно включать:
— 3 позитивных (happy path: точное совпадение, частичное, регистронезависимое).
— 3 негативных (пустой запрос, спецсимволы, очень длинный запрос >250 символов).
— 2 boundary (1 символ, 250 символов на границе, или товар на границе листа).
— 1 security (SQL-инъекция в поиске типа \`' OR '1'='1\`).
— 1 UX/usability (что показывается, когда нет результатов — empty state).

Шаблон каждого TC:
\`\`\`
TC-ID:
Title: Verify [что] when [условие]
Priority: High / Medium / Low
Type: Functional / UI / Security / UX
Preconditions: ...
Test Data: ...
Steps: 1. ... 2. ... 3. ...
Expected Result: ...
\`\`\`

<strong>Acceptance:</strong>
• "10 TC, каждый по полному шаблону IEEE 829 (все 7 полей заполнены, не «как-нибудь»). 10 — целевой тренировочный объём для master tier; если дойдёшь до 8 при сохранении 5 типов покрытия — это ОК с обоснованием."
• "Покрытие 5 типов представлено: positive / negative / boundary / security / UX. Распределение обычно ~3+3+2+1+1, но если у конкретной функции объективно нет security-аспекта (поиск ничего не записывает в БД = SQL-инъекция теряет смысл) или UX-state не выражен (нет empty state в UI) — обоснуй и оставь покрытие 8-9 типов вместо 10. Главное — не «9 позитивных + 1 что-нибудь»."
• "Title каждого TC начинается с слова \`Verify\` и содержит «what» + «when» (контекстный сценарий)."
• "Шаги (Steps) — каждый шаг — одно атомарное действие. Если в шаге 2 действия («ввести И нажать») — раздели."
• "Все TC реально выполнены на сайте, в каждом — отметка PASS/FAIL + короткий комментарий."

<strong>Подсказки:</strong>
- Anti-pattern: «TC-001: Поиск работает». Это не TC — это checklist-пункт.
  Title должен быть конкретен: «Verify search returns matching products
  when keyword exactly matches product name».
- SQL-инъекция в современных приложениях обычно не работает (server её
  экранирует), но это не значит «нет смысла тестировать». Expected
  Result для security-TC: «инъекция не проходит, поле обрабатывается
  как обычный текст, нет 500-error».
- Boundary тест на «1 символ» — реальный случай, который часто
  игнорируют. Что покажет поиск по запросу \`a\` на демо-сайте? Все товары?
  Только с буквой \`a\`? Empty state? Это уже находка для отчёта.

Если 10 TC — слишком много, но 8 — мало, делай 10. Это часть тренировки
выдержки внимания.

<strong>Self-check вопросы:</strong>
• "Какой из твоих 10 TC, провалившись, заслуживает Bug Report Severity Major или выше? Почему?"
• Какой TC автоматизировал(а) бы первым в Selenium / Playwright и почему?`,
  },
  {
    id: 'hw_s04_02',
    sprintId: 'S04',
    code: 'HW-S04-02',
    title: 'Найди и оформи 3 реальных бага',
    icon: '🐛',
    estimateMin: 30,
    priority: 'P1',
    description: 'Баг-репорт — главный артефакт работы QA. По нему PM и тимлид судят, можно ли доверять задачи без надзора. Делаем портфолио для собеса.',
    prompt: `Баг-репорт — главный артефакт работы QA. По нему PM и разработчик судят, можно ли тебе доверять задачи без надзора. После трёх реальных багов на публичном сайте у тебя будет портфолио, которое можно показать на собесе.

<strong>Что сделать:</strong>
Открой \`automationexercise.com\` или \`demoblaze.com\` (на свой выбор).
Пройди полный путь покупки — от регистрации до checkout. На каждом шаге
смотри: visual bugs (что сломано визуально), validation gaps
(форма принимает то, что не должна), broken UX (логика хромает).

Найди **3 реальных бага** и оформи каждый по полному шаблону Bug Report
из T03-T04 Sprint 04:
- Bug ID
- Title (формула: [что] + [где] + [когда])
- Severity (Critical/Major/Minor/Trivial) + обоснование
- Priority (High/Medium/Low) + обоснование
- Environment (браузер, ОС, URL)
- Preconditions
- Steps to Reproduce (нумерованный список, по одному действию)
- Expected vs Actual
- Frequency (Always / Often / Sometimes / Rare)
- Screenshots (приложить!)
- **Business Impact** — 1-2 предложения: как этот баг влияет на пользователей
  или revenue, если попадёт в production.

Среди 3 багов желательно представить **больше одного уровня severity**
(т.е. не три Trivial и не три Critical) — это тренировка калибровки.
Если за время сессии на сайте объективно нашлись только баги одного
severity-бакета — это валидное наблюдение само по себе, зафиксируй его
отдельным выводом «severity-распределение этой сессии было skewed,
потому что …», и подбери 1-2 кандидата под другие severity среди
«совсем мелких» (typo, alignment) или edge-сценариев.

<strong>Acceptance:</strong>
• 3 бага оформлены по полному шаблону. Каждое поле заполнено, не «—».
• К каждому багу приложен скриншот (не текст «там было сломано»).
• "Среди 3 багов представлено больше одного уровня severity (Major + Minor + Trivial, Critical + Major + Minor и т.д.) — либо явно зафиксировано, почему все три попали в один бакет, и перечислены кандидаты для добивки разнообразия."
• "Severity ≠ Priority хотя бы у одного бага (например, Severity=Minor, Priority=High из-за visibility — баг на главной странице)."
• "Business Impact не «пользователю неудобно» — конкретно: «при этом баге часть пользователей не сможет завершить checkout → потеря транзакций»."

<strong>Подсказки:</strong>
- Severity = техническое влияние (что сломалось). Priority = бизнес-влияние
  (когда фиксить). Классический пример: typo «Полтика конфиденциальности»
  в footer — Severity Trivial (функционал работает), Priority Medium
  (виден на каждой странице, бренд страдает).
- Если все 3 твоих бага визуальные — поищи функциональные. Если все
  функциональные — поищи UX/usability.
- Если на сайте «всё работает идеально» — углубись в edge cases: что
  при пустом каталоге? при offline? при нажатии back во время загрузки?
  Идеальных сайтов не бывает.

<strong>Self-check вопросы:</strong>
• "Если бы PM прочитал твой худший Bug Report — что бы он попросил добавить или уточнить?"
• "Какой из твоих 3 багов наиболее вероятно «не воспроизведётся» у разработчика? Почему? Что добавить?"`,
  },
  {
    id: 'hw_s04_03',
    sprintId: 'S04',
    code: 'HW-S04-03',
    title: 'Мини Test Plan для модуля «Избранное»',
    icon: '📐',
    estimateMin: 30,
    priority: 'P2',
    description: 'Test Plan — документ, который защищает QA от вопроса «а что вы вообще тестировали?» после релиза. Учимся писать минимальный план для «1 QA × 5 дней».',
    prompt: `Test Plan — это документ, который защищает QA от вопроса «а что вы вообще тестировали?» после релиза. Здесь учимся писать минимальную, но боевую версию для типичной ситуации «1 QA, 5 дней до релиза».

<strong>Что сделать:</strong>
Команде нужно протестировать новый модуль «Избранное» (wishlist) для
e-commerce за 5 рабочих дней. Команда: 1 QA (ты). Релиз через 5 дней.

Составь **mini Test Plan** по структуре T05 Sprint 04 (CTFL Ch.5).
Структура (6 секций — **наличие всех 6 обязательно**, объём внутри
секций — настолько, насколько требует контекст «1 QA × 5 дней»):

1. **Scope** — несколько строк In Scope + несколько строк Out of Scope.
   Out of Scope не сокращай: «этого не будет в этом тесте» — критическая
   защита от ожиданий.
2. **Test Strategy** — какие виды тестирования и какой % усилий на каждый.
   Несколько видов (functional, regression, cross-browser, mobile, и т.д.) —
   обычно 3-5; меньше — стратегия плоская, больше — растекается.
3. **Entry Criteria** — что должно быть готово ДО старта тестирования
   (несколько пунктов; типично 3-5).
4. **Exit Criteria** — что считается «готово к релизу» (несколько
   measurable пунктов; типично 3-5).
5. **Schedule** — таблица на 5 дней, по одному QA. Что делается в каждый
   день.
6. **Risks & Mitigations** — риски, которые могут сорвать тестирование
   (типично 3-5 в реальном Test Plan; точное число не цель — цель связка
   каждого риска с конкретным mitigation).

Формат — markdown-документ или таблица в Google Sheets.

<strong>Acceptance:</strong>
• "Все 6 секций представлены, не пропущены — это структурное требование шаблона IEEE 829 / CTFL Ch.5, не произвольный счётчик."
• "Test Strategy названа в %: «functional 60% / cross-browser 20% / mobile 15% / smoke-после-фиксов 5%». Без процентов — это не strategy, это checklist."
• "Exit Criteria — measurable: не «всё работает», а «100% P0 + P1 test cases PASS, 0 Critical / 0 Major open»."
• "Schedule — 5 строк (по одной на день), в каждой — конкретные действия QA, не «работаю»."
• "Риски сформулированы с конкретными mitigation'ами (не «риск: что-то пойдёт не так. mitigation: придумаем»). В реальном Test Plan их обычно 3-5; точное число не цель — цель практика связки риск↔mitigation."

<strong>Подсказки:</strong>
- Если Schedule всегда «Day 1: smoke, Day 2-4: functional, Day 5:
  regression» — слишком абстрактно. Конкретизируй: «Day 1 утро: review
  user stories + smoke; Day 1 день: пишу 15 P0 test cases».
- Out of Scope — критическая секция. Если её пропустить, в продакшене
  выяснится, что «никто не тестировал sharing wishlist'а». Явное
  «вне scope, в следующем релизе» — это защита от ожиданий.
- Risk: «недостаточно времени для cross-browser» → mitigation: «приоритет
  Chrome + Safari, Firefox только smoke; Edge — после релиза в неделю
  мониторинга».

<strong>Self-check вопросы:</strong>
• "Какой риск из 3+ наиболее вероятен в контексте '1 QA, 5 дней'? Что делает его самым опасным?"
• Какой Exit Criteria НЕ выполнится, если найдётся Major-баг в день 4?`,
  },
  {
    id: 'hw_s04_04',
    sprintId: 'S04',
    code: 'HW-S04-04',
    title: 'User Story → Test Scenarios → Test Cases · полная декомпозиция',
    icon: '🧬',
    estimateMin: 40,
    priority: 'P2',
    description: 'Иерархия Story → AC → Test Scenarios → Test Cases — фундамент любого формального тестирования. На CTFL её спрашивают почти в каждом билете.',
    prompt: `Иерархия Story → AC → Test Scenarios → Test Cases — это фундамент любого формального тестирования. На CTFL её спрашивают почти в каждом билете. После этой декомпозиции ты будешь видеть тестовые артефакты как дерево, не как кучу.

<strong>Что сделать:</strong>
Дана User Story (для условного e-commerce, например для каталога
Wildberries-уровня):
«As a registered user, I want to add items to my wishlist, so that I can
save products for later purchase.»

Сделай полную декомпозицию (см. T01-T03 Sprint 04):

1. Напиши **Acceptance Criteria** в формате Given/When/Then. Целевой
   объём — 5-7 AC с покрытием 4 типов (happy path, negative, edge case,
   security). Меньше 5 — обычно не хватает на все 4 типа; больше 7 —
   начинается дублирование.
   (Можно переиспользовать AC из HW-S02-02, если ты делал(а) — но
   добавь несколько новых, чтобы упражнение было свежим.)
2. Из AC выведи **Test Scenarios** (1 уровень выше Test Case —
   «что мы проверяем», без шагов). Объём обычно 7+, потому что из
   одного AC выводится 2-3 scenarios (например, AC «happy path добавления»
   → TS-01 «add from product page», TS-02 «add from search results»,
   TS-03 «add from category list»).
   Шаблон: TS-ID · Scenario name · Type (positive/negative/security/...).
3. Для **3 любых сценариев** напиши полные **Test Cases** (тот же шаблон,
   что в HW-S04-01: ID + Title + Priority + Preconditions + Steps + Expected).

Формат вывода — markdown-документ из 3 секций: AC / Scenarios / Test Cases.
Это упражнение в иерархии: Story → AC → TS → TC (CTFL Ch.5).

<strong>Acceptance:</strong>
• "AC сформулированы в формате Given/When/Then, покрытие 4 типов представлено (happy / negative / edge / security). Целевой объём — 5-7 AC; если получилось меньше при сохранении 4 типов — обоснуй."
• "Test Scenarios — высокоуровневые («Verify wishlist persists after logout»), не низкоуровневые шаги. Объём — обычно 7+ потому что из одного AC выводится 2-3 scenarios; если у тебя AC = scenarios один к одному, ты не довёл(а) декомпозицию до уровня TS."
• "3 полных Test Cases — каждый помечен, какой Test Scenario он реализует (TS-01 → TC-001, TC-002; TS-02 → TC-003)."
• "Иерархия чёткая: 1 Story → AC → Scenarios → Test Cases (можно нарисовать как tree). Это структурное требование декомпозиции, не произвольные числа."

<strong>Подсказки:</strong>
- Test Scenario НЕ равен Test Case. Scenario — «Verify removing an item
  from wishlist updates the count immediately». Это ≥2 TC: один с
  одним товаром, другой с несколькими, третий с offline-состоянием.
- Если у тебя 5 AC и 5 Scenarios — недокатил(а). Из одного AC обычно
  выводят 2-3 Scenario. Например, AC «happy path добавления» → TS-01
  «add from product page», TS-02 «add from search results», TS-03
  «add from category list».
- Иерархия Story/AC/Scenarios/TC — это CTFL Ch.5 territory. На собесе
  junior'у часто рисуют коробочку «Story» и просят разложить — ты
  сейчас тренируешь именно этот навык.

<strong>Self-check вопросы:</strong>
• "Какой Test Scenario из 7 содержит больше всего скрытых TC? Сколько ты бы написал(а), если бы делал(а) полное покрытие?"
• "Какие 4 из 7 Scenarios ты прогонял(а) бы при regression-тестировании (после фикса другого бага)?"`,
  },
  {
    id: 'hw_s05_01',
    sprintId: 'S05',
    code: 'HW-S05-01',
    title: 'Equivalence Partitioning + BVA для формы регистрации',
    icon: '🔬',
    estimateMin: 40,
    priority: 'P1',
    description: 'EP + BVA — пара техник, на которой строится тестирование любой формы. ISTQB любит их экзаменовать. Тренируем на форме регистрации.',
    prompt: `EP + BVA — пара техник, на которой строится тестирование любой формы. ISTQB любит их экзаменовать. После этой формы регистрации ты будешь автоматически разбивать любое поле на классы эквивалентности и проверять границы.

<strong>Что сделать:</strong>
Дана форма регистрации с полями:
— **Имя** (2-30 символов, только буквы и пробелы, латиница или кириллица).
— **Email** (формат \`xxx@xxx.xx\`, максимум 254 символа — RFC 5321 limit).
— **Пароль** (8-20 символов, минимум 1 цифра + 1 заглавная + 1 спецсимвол
  из набора \`!@#$%^&*\`).
— **Возраст** (целое число, 14-100 лет).

Для **каждого из 4 полей** определи:
1. **Equivalence Partitioning (EP)** — все классы эквивалентности (valid +
   invalid). Минимум 3 класса на поле.
2. **BVA — 3-value boundary analysis** — для каждой границы 3 значения:
   boundary-1, boundary, boundary+1. Не «just-в-точке» (это outdated 1980s
   2-value); используем 3-value (CTFL Ch.4 syllabus).
3. **Минимальный набор тестов** — итог: какие тест-данные достаточны для
   покрытия всех EP + BVA.

Формат вывода — markdown-таблица или Google Sheets. По одной таблице
на каждое поле.

<strong>Acceptance:</strong>
• "≥3 EP-класса на каждое из 4 полей (всего ≥12 классов)."
• "BVA 3-value применён ко всем числовым границам (длина, возраст). Не 2-value, проверь это явно — у тебя 3 значения на границу: 1, 2, 3 для границы 2."
• "Итоговое 'минимальное покрытие' указано в конце каждой таблицы — это список тест-данных, который ты бы реально написал(а) в Test Case."
• "Хотя бы один класс — Unicode-related (имя кириллицей валидное, имя с эмоджи 😀 — invalid? зависит от спецификации)."

<strong>Подсказки:</strong>
- EP без BVA — типичная ошибка junior'а. EP даёт «классы»; BVA проверяет
  КОНКРЕТНУЮ границу класса. Они дополняют друг друга, не заменяют.
- Email регулярка — это adversarial-territory. Спецификация RFC 5322
  разрешает много нестандартных вещей (\`a@[127.0.0.1]\` — валидно по RFC,
  но многие сервисы отвергают). Для junior — упрощай: «есть @, есть
  точка после @, не пустое».
- Пароль из 8 символов «aaaaaaaa» — невалидный (нет цифры, заглавной,
  спецсимвола), но boundary-проход по длине. Это пример того, как
  EP + BVA взаимодействуют: технически на boundary, но провал EP.

<strong>Self-check вопросы:</strong>
- "Какое поле дало больше всего тест-данных в финальной выборке? Почему?"
- "Какой класс эквивалентности ты бы НЕ тестировал(а) при time-boxed
  регрессии (Sprint 04 stuff)?"`,
  },
  {
    id: 'hw_s05_02',
    sprintId: 'S05',
    code: 'HW-S05-02',
    title: 'Decision Table для банковского перевода',
    icon: '🧮',
    estimateMin: 30,
    priority: 'P1',
    description: 'Decision Table — техника для проверки логики «если X, и Y, и Z, то...». Особенно полезна в банковской и compliance-территории, где пропуск одной комбинации = регуляторный риск.',
    prompt: `Decision Table — техника для проверки логики «если X, и Y, и Z, то...». Особенно полезна в банковской и compliance-территории, где пропуск одной комбинации = регуляторный риск. После этого упражнения у тебя будет полная таблица + сокращённая до минимального покрытия — артефакт для собеса.

<strong>Что сделать:</strong>
Условия банковского перевода (4 булевых):
— Баланс достаточен?
— Получатель существует в системе?
— Сумма в дневном лимите (≤ 100 000 ₽)?
— 2FA пройдена в последние 5 минут?

Постройте полную **Decision Table** (4 условия = 2⁴ = 16 комбинаций).
Для каждой комбинации:
1. Expected Result (что система должна сделать):
   — Перевод успешен.
   — Ошибка: недостаточно средств.
   — Ошибка: получатель не найден.
   — Ошибка: превышен лимит (предложить разбить).
   — Требуется 2FA.
   — (другие варианты, если вы их обнаружите).
2. Mark «возможна ли эта комбинация на практике» (например, если сумма >
   лимита, то «баланс достаточен» автоматически проверяется ПОСЛЕ — может
   быть «не достижимо»).
3. Test Case ID — короткое название теста.

Формат вывода — таблица 6+ колонок: # · Условие 1 · 2 · 3 · 4 · Expected ·
Reachability · TC name.

Затем **сократи Decision Table** до **минимального покрытия** (только rules,
которые дают РАЗНЫЕ Expected Results) — это CTFL Ch.4 «Decision Table
Testing».

<strong>Acceptance:</strong>
• "Все 16 комбинаций перечислены в полной таблице."
• "Для каждой — Expected Result и Reachability."
• "Security-сценарии явно помечены (пометка «Security» / «Fraud-related») — например, перевод без 2FA = security-issue, не «обычная ошибка»."
• "Минимальное покрытие выделено отдельной колонкой или таблицей. Чисто меньше 16 (обычно 6-8) — каждый rule покрывает уникальный Expected."
• "Обоснование указано там, где rule помечен «не достижим на практике» (если такие rules в твоей таблице есть; если все 16 объективно достижимы — это тоже валидный вывод, отметь его явно)."

<strong>Подсказки:</strong>
- Если у тебя 16 разных Expected Results — что-то не так. В реальной
  системе много комбинаций «свернутся» в одну ошибку (например, при
  отсутствии 2FA уже неважны баланс/получатель/лимит — отказ всё равно).
- Минимизация = выкинуть rule, который дублирует Expected другого rule
  при тех же релевантных условиях. Это математика, не интуиция.
- Reachability важна: «баланс ниже лимита, но получатель не существует» —
  может быть достижимо, но возможно UI не позволит ввести получателя
  вообще, тогда rule не достижим.

<strong>Self-check вопросы:</strong>
- "Какой rule самый опасный для пропустить в тестировании (compliance/fraud)?"
- "Если бы ты добавил(а) 5-е условие — что бы это было? Сколько строк
  стала бы таблица?"`,
  },
  {
    id: 'hw_s05_03',
    sprintId: 'S05',
    code: 'HW-S05-03',
    title: 'State Transition для аккаунта + Pairwise для фильтра',
    icon: '🔄',
    estimateMin: 40,
    priority: 'P2',
    description: 'State Transition + Pairwise — две техники, которые превращают «много возможных сценариев» в управляемый набор тестов.',
    prompt: `State Transition + Pairwise — две техники, которые превращают «много возможных сценариев» в управляемый набор тестов. После этого упражнения ты сможешь обосновать, почему 12 тестов покрывают 144 комбинации.

<strong>Что сделать:</strong>
**Часть А — State Transition.**
Аккаунт пользователя имеет состояния: **Active**, **Suspended**, **Blocked**,
**Deleted**, **PendingEmailConfirmation** (новое — впервые после регистрации).

Нарисуй полную диаграмму (Mermaid / draw.io / paper + photo). Для каждого
перехода укажи:
— Триггер (какое действие переход вызывает: «3 неудачных логина», «admin
  решение», «истекло 24 часа без подтверждения email»).
— Test Case ID (короткое имя теста).

Найди **невалидные переходы** (которые НЕ должны работать, но их нужно
протестировать — security-territory). Например: Deleted → Active (попытка
восстановления удалённого аккаунта) — должно отвергаться. Сколько именно
невалидных найдётся — зависит от 5 состояний и логики системы; для
5 состояний типично 4-6 невалидных пар, форсировать «минимум N» не нужно.

**Часть Б — Pairwise.**
Каталог фильтра e-commerce: 4 параметра.
— Категория: Электроника / Одежда / Книги (3 значения)
— Размер: XS / S / M / L (4 значения)
— Цвет: Чёрный / Белый / Красный (3 значения)
— Сортировка: Цена ↑ / Цена ↓ / Популярность / Новинки (4 значения)

Полный перебор: 3×4×3×4 = 144 теста. Используй онлайн-инструмент
\`pairwise.yuuniworks.com\` (или Microsoft PICT) — получи Pairwise-набор
и зафиксируй итоговое число тестов. Это должно быть значительно меньше
144 (обычно ~12-16).

Формат вывода — два markdown-документа в одном файле.

<strong>Acceptance:</strong>
• "Часть А: диаграмма покрывает все 5 состояний и переходы между ними; невалидные переходы явно помечены как «должны быть rejected». Объём валидных переходов для 5 состояний обычно 5-8, невалидных — 4-6 (security-territory); это вытекает из количества состояний, не из произвольной квоты."
• "Триггеры названы конкретно (не «событие 1»)."
• "Часть Б: pairwise-набор сгенерирован, число тестов указано. Скриншот или копия выходных данных приложена."
• "Расчёт сокращения: «полный перебор N тестов → pairwise M тестов, сокращение в N/M раз»."

<strong>Подсказки:</strong>
- State Transition — дайте phantom-state PendingEmailConfirmation. Из
  него — таймаут на удаление через 24h (auto-Deletion), либо — переход в
  Active при подтверждении.
- Невалидный переход = «UI должен это запрещать или backend отвергать
  с 4xx». Например: payload-инжекция в API напрямую — что произойдёт?
  Это security-test поверх state-transition.
- Pairwise — 90% багов вызываются взаимодействием ПАР параметров, не
  тройками. Поэтому даже сильно сокращённое покрытие (12 из 144) очень
  эффективно.

<strong>Self-check вопросы:</strong>
- "Какой невалидный переход в твоей State Transition диаграмме самый
  опасный, если он случайно работает?"
- "Pairwise — много меньше тестов, но какие баги он точно НЕ найдёт
  (что закладывается в trade-off)?"`,
  },
  {
    id: 'hw_s05_04',
    sprintId: 'S05',
    code: 'HW-S05-04',
    title: 'Метрики тестового цикла + Test Summary Report',
    icon: '📊',
    estimateMin: 30,
    priority: 'P3',
    description: 'Test Summary Report — документ, на основе которого PM решает «релизим или ждём». Метрики должны быть точными и интерпретируемыми.',
    prompt: `Test Summary Report — это документ, на основе которого PM решает «релизим или ждём». Метрики должны быть точными и интерпретируемыми. Здесь упражнение в расчёте, интерпретации и финальной рекомендации GO / NO GO.

<strong>Что сделать:</strong>
Допустим, ты только что закрыл(а) тестовый цикл для условной фичи
«Сохранённые методы оплаты в e-commerce». Условные результаты (выдуманные
для упражнения, framing «допустим»):

- Запланированных Test Cases: 80
- Выполнено: 75 (5 заблокированы багом BUG-101, который не успели починить).
- Passed: 62, Failed: 13.
- Найдено багов: 24 (Critical: 2, Major: 6, Minor: 11, Trivial: 5).
- Из 24 — 18 закрыты к концу цикла, 6 — open (1 Critical, 2 Major, 3 Minor).
- Общее число требований в фиче: 12, покрыто Test Cases: 11 (1 не покрыто —
  «уведомление о смене метода оплаты»).
- Reopened багов за цикл: 3 из 18 «закрытых» → 3 / 18 ≈ 16.7% reopen rate.

Рассчитай метрики (см. T-метрики Sprint 05):
1. Test Case Pass Rate
2. Test Coverage (по требованиям)
3. Defect Density (по фиче, не KLOC)
4. Bug Reopen Rate
5. (опционально) Defect Leakage если 1 баг ушёл в production через
   неделю — посчитай, что станет с метрикой.

Затем напиши **Test Summary Report** (1 страница):
— Executive Summary (3-5 строк: что протестировано, ключевые цифры,
  рекомендация GO / NO GO для релиза).
— Метрики (таблица).
— Open Risks — что не покрыто, что блокирует, что страшно.
— Recommendation: **GO** / **NO GO** / **GO с условием** (с обоснованием).

<strong>Acceptance:</strong>
• "Все 4 (или 5 с опциональным Defect Leakage) метрики посчитаны. Формула + цифра + интерпретация (1 строка: что говорит эта цифра)."
• "Executive Summary 3-5 строк, не больше — это для CEO/PM, у которого 30 секунд."
• "Recommendation чётко обоснована — не просто «GO», а «GO с условием: Critical bug BUG-X должен быть починен перед релизом»."
• "Open Risks — секция заполнена конкретными пунктами (что не покрыто, что блокирует, что страшно). Типично 3-5; точное число не цель — цель в том, чтобы Executive Summary мог сослаться на риски, а не на «всё хорошо»."

<strong>Подсказки:</strong>
- Pass Rate = 62 / 75 = 82.7%. Это middle: индустриальный benchmark
  обычно 90%+ для готового релиза. 82% означает «есть стабильность,
  но не ready».
- Coverage = 11 / 12 = 91.7%. Не «полное», но близко. Один требование
  не покрыто — это GAP, должен быть в Open Risks.
- GO / NO GO решение: 1 Critical open + 2 Major open = NO GO для
  релиза в моей школе мысли. Argument: production-impact слишком велик.
  Альтернатива — GO с workaround / hotfix-планом.

<strong>Self-check вопросы:</strong>
- "Какая из 4 метрик самая «обманчивая» — может выглядеть хорошо, а
  скрывать проблемы? Почему?"
- "Если PM скажет «у нас релиз через 2 дня, можем выкатить?» — что
  ответишь, опираясь на эти цифры?"`,
  },
  {
    id: 'hw_s06_01',
    sprintId: 'S06',
    code: 'HW-S06-01',
    title: 'План адресного повтора после Mock Exam #1',
    icon: '🎓',
    estimateMin: 30,
    priority: 'P1',
    description: 'План адресного повтора после Mock Exam #1 · фокус на самых слабых главах, не равномерное «повторю всё».',
    prompt: `После Mock Exam важно не «повторить всё», а «закрыть конкретные слабые места». Этот адресный план экономит дни до Mock #2 и снижает риск burnout перед реальным экзаменом ISTQB.

<strong>Что сделать:</strong>
После прохождения Mock Exam #1 (40 вопросов, тикет T03 в Sprint 06)
у тебя на руках — список ответов с пометками PASS/FAIL и привязкой к
главам ISTQB Syllabus 4.0:
— Ch.1 Fundamentals of Testing
— Ch.2 Testing Throughout SDLC
— Ch.3 Static Testing
— Ch.4 Test Techniques
— Ch.5 Test Management
— Ch.6 Test Tools

Твоя задача — **построить targeted re-study план** на следующие 3-5 дней
до Mock Exam #2:

1. **Анализ.** Какие 2 главы дали больше всего ошибок? Какой K-уровень
   (K1 recall / K2 understand / K3 apply) тебя подвёл? (Если 80%+ ошибок
   на K3-вопросах — твоя проблема в применении, не в запоминании.)
2. **Топ-3 темы для re-study.** Внутри слабых глав — назови 3 конкретные
   темы (не «Ch.4», а «Decision Table — построение и минимизация»). Для
   каждой — 1-2 действия: перечитать секцию X в курсе, прорешать N
   дополнительных вопросов из Quiz Banks (Slot C), посмотреть Lab
   LAB-S05-02.
3. **Расписание.** День 1 / 2 / 3 (можно 5) — что делаешь, сколько
   времени, какой ожидаемый эффект.

Формат — markdown-таблица или один документ.

<strong>Acceptance:</strong>
• "Анализ ошибок по главам и K-уровню сделан (можно сделать «допустим у меня было…», если ты ещё не дошёл(шла) до Mock #1; framing «допустим» обязателен)."
• "Темы для re-study — конкретные (не «Ch.4 в целом», а «Decision Table — построение и минимизация»). Целевой объём — 3-5 тем: меньше 3 — план слишком узкий (риск пропустить смежные слабые места); больше 5 — нет времени на нормальное re-study до Mock #2."
• "Расписание реалистичное: ≤90m в день re-study, иначе burnout до Mock #2."
• "Для каждой темы зафиксировано конкретное действие (не «повторить», а «прорешать N вопросов из Quiz Banks T-Q4», «посмотреть LAB-S05-02»)."

<strong>Подсказки:</strong>
- Самая частая слабая тема у джуниоров на CTFL — Ch.4 (test techniques),
  потому что K3-вопросы требуют применения, не recall. Если у тебя
  ошибки именно там — это нормально.
- K-уровни: K1 — «определи термин», K2 — «объясни разницу», K3 — «выбери
  правильную технику для ситуации», K4 — «проанализируй» (на CTFL
  Foundation редко). Свои ошибки разбирай по K-level: если все на K3,
  ты понял(а) теорию, но не натренировал(а) применение — нужно больше
  Lab-упражнений.
- 90 минут в день — порог. Больше — накапливается усталость, retention
  падает (когнитивная нагрузка). Лучше 4 раза по 60m, чем 1 раз по 4h.

<strong>Self-check вопросы:</strong>
- "Что было сложнее — анализ ошибок или выбор тем для re-study?"
- "Если бы у тебя был всего 1 день до Mock #2 — на что бы потратил(а)?"`,
  },
  {
    id: 'hw_s06_02',
    sprintId: 'S06',
    code: 'HW-S06-02',
    title: 'Spaced repetition · колода из топ-30 терминов',
    icon: '🃏',
    estimateMin: 25,
    priority: 'P2',
    description: 'Spaced repetition — самый эффективный способ запомнить терминологию ISTQB в долгую. 30 хорошо проработанных карточек > 100 поверхностных.',
    prompt: `Spaced repetition — самый эффективный способ запомнить терминологию в долгую перспективу. Не делай 100 карточек: 30 хорошо проработанных запоминаются лучше, чем 100 поверхностных, и не съедают день на прогон.

<strong>Что сделать:</strong>
Из глобального ISTQB Glossary на 155 терминов (тикет T02 в Sprint 06)
выбери **30 самых сложных для тебя** терминов. Это термины, на которые
ты:
— путаешься между двумя похожими (severity vs priority, smoke vs sanity,
  validation vs verification, EP vs BVA).
— не уверен(а) в формальном определении ISTQB (можешь объяснить «своими
  словами», но не точно по syllabus).
— на mock'е выбрал(а) неправильный вариант, потому что неверно понял(а)
  термин.

Создай **flashcards** в любом инструменте (Anki / Quizlet / paper-cards
/ Notion). Каждая карточка:
- **Front:** термин (английский) + краткий контекст (1 фраза «когда
  встречается»).
- **Back:** определение (по ISTQB-style, 1-2 предложения, своими словами,
  БЕЗ дословных цитат) + типичная путаница / pitfall (1 строка «часто
  путают с X, потому что…»).

Прогон: 5-7 дней spaced repetition (Anki сам распределит интервалы).
Ежедневно — 5-10 минут, не больше.

Формат вывода — экспорт колоды в файл (Anki .apkg / Quizlet share-link /
скриншот колоды) + журнал «прогон 1 — 30 карточек, retention X%».

<strong>Acceptance:</strong>
• "Колода ≥30 карточек создана."
• "Каждая карточка имеет Front (термин + контекст) и Back (определение + pitfall) — не пустые поля."
• "Минимум 5 карточек посвящены классическим путаницам (severity vs priority, validation vs verification, EP vs BVA, smoke vs sanity, retesting vs regression)."
• "Прогон ≥3 дней — журнал retention'а заполнен (хотя бы число «правильных из 30» на каждый день)."

<strong>Подсказки:</strong>
- Anki — золотой стандарт для spaced repetition. Бесплатный, есть mobile
  app, алгоритм проверен десятилетиями (SM-2 / FSRS).
- Если используешь paper-cards — нумерация и перемешивание по дням
  (день 1: все 30; день 2: те, что не запомнил(а); день 4: ещё раз; день 7:
  финальный прогон).
- Pitfall важнее определения. Знание «X — это…» без знания «часто путают
  с Y» = 50% знания. ISTQB любит вопросы вида «выбери, чем X отличается
  от Y» — именно для проверки этой второй половины.
- Не делай 100 карточек. 30 хорошо проработанных > 100 поверхностных.
  Спокойно, оставшиеся 125 терминов ты уже знаешь по Sprint 01-05.

<strong>Self-check вопросы:</strong>
- "После 3 дней — какие 5 карточек упорно не запоминаются? Что у них общего?"
- "Если бы ты делал(а) колоду для друга — что бы изменил(а) (другой набор сложных тем)?"`,
  },
  {
    id: 'hw_s07_01',
    sprintId: 'S07',
    code: 'HW-S07-01',
    title: 'Severity / Priority + жизненный цикл · 5 сценариев',
    icon: '⚖️',
    estimateMin: 25,
    priority: 'P1',
    description: 'Калибровка Severity / Priority + состояния жизненного цикла на 5 коротких сценариях · разогрев для собеседования.',
    prompt: `Калибровка Severity и Priority — то, на чём джунов чаще всего спрашивают на собесе и где они теряют доверие тимлида в первый месяц. После этих пяти сценариев ты будешь выставлять и то, и другое без раздумий.

<strong>Что сделать:</strong>
Закрепи T01 (Severity vs Priority) + T02 (состояния жизненного цикла) на 5 коротких сценариях. Для каждого — определи Severity (Critical / Major / Minor / Trivial) + Priority (P0 / P1 / P2 / P3) + напиши 1-2 строки обоснования. В сценарии 1.5 — назови состояние и обоснуй одной строкой.

<strong>Сценарии:</strong>
• 1.1 — на сайте в подвале опечатка «Pivacy Policy». До маркетинговой кампании 2 дня. Определи Severity, Priority, обоснование.
• 1.2 — на checkout у 100% мобильных пользователей не работает кнопка Pay (на desktop работает). Определи.
• 1.3 — админский отчёт не загружается, если диапазон дат больше 5 лет · senior пользуется один раз в год. Определи.
• 1.4 — после успешной оплаты пользователь видит «Order #undefined» вместо реального ID · в 5% случаев. Определи.
• 1.5 — разработчик пофиксил баг в ветке, влил в master, билд выложен на staging. Какое состояние у тикета?

<strong>Acceptance:</strong>
• 5 сценариев · для каждого — Severity + Priority + 1-2 строки обоснования.
• Сценарий 1.5 — название состояния + 1 строка обоснования (это не калибровка, это вопрос про жизненный цикл).
• Хотя бы в одном сценарии Severity ≠ Priority (тренировка калибровки · классический паттерн «опечатка в самом видном месте»).

<strong>Подсказки:</strong>
- Severity = техническое влияние (что сломалось). Priority = бизнес-влияние (когда чинить). Классический пример: опечатка в подвале — Severity Trivial, Priority Medium (страдает бренд).
- Баги только на мобильной версии в checkout — почти всегда High Priority (часть транзакций потеряна), но Severity зависит от того, есть ли обходной путь: кнопка не работает, но можно оплатить с desktop — Major; обхода нет — Critical.
- В сценарии 1.5 состояние ещё не «Done» · впереди верификация QA. Это «Resolved» или «Awaiting QA verification» в зависимости от workflow команды.

<strong>Self-check вопросы:</strong>
- В каком из 5 сценариев самый большой разрыв между Severity и Priority? Почему?
- Сценарий 1.5 · какое следующее действие QA в этом состоянии?`,
  },
  {
    id: 'hw_s07_02',
    sprintId: 'S07',
    code: 'HW-S07-02',
    title: '3 баг-репорта по полному 11-полевому шаблону',
    icon: '🐛',
    estimateMin: 30,
    priority: 'P1',
    description: 'Закрепление полного баг-репорта на 11 полей (Sprint 04 база + Sprint 07 T03 расширения).',
    prompt: `Полный баг-репорт на 11 полей — то, по чему PM поймёт, можно ли тебя оставить наедине с проектом. После трёх мини-багов ты будешь собирать полный шаблон по памяти.

<strong>Что сделать:</strong>
Закрепи навык написания полного баг-репорта на 11 полей (S04 база + S07 T03 расширения). 3 мини-бага · для каждого — полный баг-репорт с явным списком приложений (что приложить как evidence).

<strong>Баги:</strong>
• 2.1 — баг «кэшбэк не списывается» · напиши полный баг-репорт на 11 полей с явным списком приложений.
• 2.2 — баг «в order summary итоговая сумма не обновляется после применения промокода» (образец ответа в T10 Mission · напиши независимо, не копируй).
• 2.3 — баг по выбору · из Atelier scenario · полный баг-репорт на 11 полей.

<strong>Acceptance:</strong>
• 3 баг-репорта оформлены по полному шаблону на 11 полей: Title по формуле · Pre-conditions · Steps (атомарные) · Expected / Actual · Severity · Priority · Environment · Frequency · Build · Business Impact · Attachments.
• Каждый баг-репорт содержит явный список приложений (что приложить: screenshot / HAR / SQL / console log) — не «приложу скриншот», а «приложу: 1) скриншот профиля до и после refresh, 2) DevTools Network HAR для GET /api/cashback, 3) SQL-вывод SELECT * FROM cashback_ledger WHERE user_id = ...».
• Сценарий 2.2 — независимо от T10 sample (не копировать · своя формулировка): тренировка переноса паттерна.

<strong>Подсказки:</strong>
- Формула Title (T03): [Что] + [где] + [когда]. Анти-паттерн: «Кэшбэк не работает». Правильно: «Cashback не списывается из profile.balance после redeem на странице /cashback · 100% случаев».
- Business Impact ≠ «пользователю неудобно». Конкретно: «при этом баге часть транзакций потеряна → revenue loss / compliance risk / churn».
- Frequency = Always / Often / Sometimes / Rare — это рассчитанная цифра, не «иногда».

<strong>Self-check вопросы:</strong>
- Если бы PM прочитал твой худший баг-репорт — что бы он попросил уточнить?
- Какой из 3 баг-репортов скорее всего «не воспроизведётся» у разработчика? Почему? Что добавить?`,
  },
  {
    id: 'hw_s07_03',
    sprintId: 'S07',
    code: 'HW-S07-03',
    title: 'Мини-расследование бага + оформить в Linear',
    icon: '🔍',
    estimateMin: 35,
    priority: 'P2',
    description: 'Применить T08 процесс расследования на меньшем масштабе · оформить в Atelier sandbox-tracker как Linear-style issue.',
    prompt: `Расследование бага — половина работы QA. Не «нашёл и отдал», а «нашёл, копнул, отдал так, чтобы разработчик сразу понял». Здесь упражнение на меньшем масштабе, чем T10 Mission — для отработки потока.

<strong>Что сделать:</strong>
Примени 5-шаговый процесс расследования из T08 на меньшем масштабе · оформи результат в Atelier sandbox-tracker как Linear-style issue.

<strong>Шаги:</strong>
• Шаг 1 (~5 мин) — открой Atelier scenario · выбери 1 баг (желательно отличный от T10 Mission).
• Шаг 2 (~10 мин) — расследование · DevTools / SQL если применимо · собери приложения (evidence).
• Шаг 3 (~15 мин) — напиши полный баг-репорт на 11 полей + (если случай сложный) короткий SFDIPOT-анализ влияния на смежные фичи.
• Шаг 4 (~5 мин) — оформи в Atelier sandbox-tracker как Linear issue · добавь метки (bug · area:X · severity:Y) · свяжи с похожими issues если есть.

<strong>Acceptance:</strong>
• Скриншот Atelier issue (с метками · severity tag · связями если есть).
• Текстовый дамп полного баг-репорта на 11 полей (все 11 полей).
• 1 абзац рефлексии «что нашла, как расследовала, что добавила сверх базового шаблона» (например, короткая SFDIPOT-заметка, или callout о регрессионном риске, или duplicate-check).

<strong>Подсказки:</strong>
- HW-3 — упрощённая версия Mission T10. HW = тренировка потока на одном дополнительном баге. Mission = полное интегративное упражнение.
- Метки в Linear обычно: тип (bug/feature/chore) · area (auth/checkout/profile) · severity (P0/P1/P2/P3). Если в Atelier sandbox-tracker нет всех типов меток — запиши «labels: ...» текстом в issue.

<strong>Self-check вопросы:</strong>
- Какую гипотезу ты подтвердила первой? Сколько времени заняло расследование?
- Что бы изменила в своём процессе во второй раз?`,
  },
  {
    id: 'hw_s08_01',
    sprintId: 'S08',
    code: 'HW-S08-01',
    title: 'SQL разогрев · 5 запросов (SELECT / WHERE / ORDER BY / NULL / LIKE)',
    icon: '🔬',
    estimateMin: 30,
    priority: 'P1',
    description: 'Закрепление базовых SQL операторов (T04 + T05) на 5 запросах · фундамент для проверки гипотез о данных.',
    prompt: `SQL — это не «знание синтаксиса», а инструмент проверки гипотез. Эти 5 запросов закрепляют базу: фильтрация, сортировка, NULL trap, LIKE. После этой разминки ты будешь читать чужие запросы и писать свои без шпаргалки.

<strong>Что сделать:</strong>
Напиши и прогони 5 запросов в Atelier sql-playground (та же БД, что в Mission). Приложи результаты как короткие скриншоты или текстовые дампы.

<strong>Запросы:</strong>
• 1.1 — найди 3 самых дорогих оплаченных заказа за последние 7 дней. Подсказка: SELECT id, total, status, created_at FROM orders WHERE status='paid' AND created_at >= ... ORDER BY total DESC LIMIT 3.
• 1.2 — найди уникальные значения колонки \`status\` в таблице orders. Подсказка: DISTINCT (T04).
• 1.3 — найди заказы с суммой между 100 и 500, отсортируй по сумме ASC. Подсказка: BETWEEN (T05 specialные операторы).
• 1.4 — найди заказы, у которых promo_code не указан (NULL). Подсказка: IS NULL (T05 NULL trap — НЕ через \`=\`).
• 1.5 — найди заказы, у которых email клиента заканчивается на '@gmail.com'. Подсказка: JOIN с users + LIKE '%@gmail.com' (T05 LIKE wildcards + T06 INNER JOIN).

<strong>Acceptance:</strong>
• Для каждого пункта — текст запроса + скриншот или текстовый дамп результата (5-10 строк).
• Если запрос вернул 0 строк, напиши предположение почему (часто это не баг, а особенность тестовых данных).
• Запрос 1.4 запрещено писать через \`promo_code = NULL\` (NULL trap · T05) · должно быть \`IS NULL\`.

<strong>Подсказки:</strong>
- NULL trap: \`WHERE x = NULL\` всегда возвращает 0 строк, потому что NULL не сравнивается оператором \`=\`. Только \`IS NULL\` / \`IS NOT NULL\` работают.
- LIKE wildcards: \`%\` — любое число символов, \`_\` — один символ. \`'%@gmail.com'\` — заканчивается на @gmail.com.
- Не запускай UPDATE / DELETE без BEGIN/ROLLBACK — привычку вырабатываем здесь.

<strong>Self-check вопросы:</strong>
- В запросе 1.4 · что вернёт \`WHERE promo_code != 'STUDIO10'\`? Включит ли это NULL? Почему?
- Если запрос 1.5 вернул 0 строк · какие 2 гипотезы ты проверила бы?`,
  },
  {
    id: 'hw_s08_02',
    sprintId: 'S08',
    code: 'HW-S08-02',
    title: 'JOIN + GROUP BY · 4 запроса',
    icon: '🔗',
    estimateMin: 30,
    priority: 'P1',
    description: 'Закрепление JOIN (T06) + агрегаций (T07) + HAVING на 4 запросах · переход от одной таблицы к нескольким.',
    prompt: `Большинство реальных вопросов от PM и аналитиков требуют JOIN двух-трёх таблиц и агрегацию. После этих 4 запросов ты сможешь отвечать «сколько X у каждого Y» без подглядывания в Stack Overflow.

<strong>Что сделать:</strong>
Напиши 4 запроса · примени LEFT JOIN · AVG · COUNT · GROUP BY · HAVING · LEFT JOIN + IS NULL для поиска «сирот».

<strong>Запросы:</strong>
• 2.1 — сколько заказов у каждого пользователя? (включая пользователей без заказов · у них 0). Подсказка: LEFT JOIN + COUNT(o.id) + GROUP BY (T06 + T07 паттерн LEFT-JOIN-COUNT).
• 2.2 — какая средняя сумма заказов в каждой стране? Подсказка: AVG + GROUP BY (T07).
• 2.3 — найди страны, где было ≥ 3 оплаченных заказов за последние 30 дней. Подсказка: WHERE до GROUP BY + HAVING после (T07 HAVING vs WHERE).
• 2.4 — найди пользователей без единого заказа в БД. Подсказка: LEFT JOIN + IS NULL (T06 — поиск «сирот»).

<strong>Acceptance:</strong>
• Запросы текстом + результат + 1 строка интерпретации каждого («это значит X, что говорит о Y»).
• В запросе 2.3 — WHERE и HAVING обе используются. WHERE до GROUP BY (фильтрует строки), HAVING после (фильтрует группы). Если обе в WHERE — синтаксическая ошибка.
• В запросе 2.4 — LEFT JOIN + WHERE o.id IS NULL · не NOT IN (анти-паттерн для NULL в подзапросе).

<strong>Подсказки:</strong>
- LEFT-JOIN-COUNT: COUNT(o.id) · не COUNT(*). COUNT(*) даст 1 для пользователя без заказов (NULL-строка тоже считается), COUNT(o.id) — корректный 0.
- HAVING — для условий на агрегатах (\`HAVING COUNT(*) >= 3\`). WHERE — для условий на строках до агрегации (\`WHERE status='paid'\`).
- LEFT JOIN + IS NULL = «найди строки левой таблицы без пары в правой» — самый частый паттерн поиска «сирот».

<strong>Self-check вопросы:</strong>
- В запросе 2.1 · если использовать INNER JOIN вместо LEFT · какие пользователи пропадут из результата? Почему?
- Запрос 2.3 · попробуй переписать через WHERE EXISTS подзапрос. Какой вариант читабельнее?`,
  },
  {
    id: 'hw_s08_03',
    sprintId: 'S08',
    code: 'HW-S08-03',
    title: 'Мини-расследование бага через SQL · 1 гипотеза + 2 запроса',
    icon: '🐛',
    estimateMin: 30,
    priority: 'P2',
    description: 'Применить 5-шаговый процесс расследования (T09) на меньшем масштабе через SQL · от жалобы пользователя до вывода.',
    prompt: `Половина SQL-работы QA — это «прийди в БД и проверь, что реально произошло». Здесь упражнение в дисциплине: сначала гипотеза, потом запрос. Не «погляжу, что в таблице» — это другой режим.

<strong>Что сделать:</strong>
Примени 5-шаговый процесс расследования (T09) на меньшем масштабе через SQL.

<strong>Сценарий:</strong>
В Customer Support поступила жалоба: «не получил уведомление по email после оформления заказа». User_id клиента известен (можно использовать любого пользователя из playground · например, alice@example.com).

<strong>Шаги:</strong>
• Шаг 1 (~5 мин) — сформулируй 2-3 гипотезы возможной причины. Например: (A) email вообще не был добавлен в email_queue, (B) email был добавлен, но job не отправил, (C) заказ не был оформлен (баг UI? клиент ошибся?).
• Шаг 2 (~10 мин) — напиши запрос для проверки гипотезы A или B. Подсказка: ищи в таблице events / email_queue (если есть в playground) для конкретного user_id.
• Шаг 3 (~5 мин) — на основе запроса · какую гипотезу подтвердила / опровергла? Запиши вывод в 2-3 предложения.
• Шаг 4 (~5 мин) — если гипотеза не подтвердилась · напиши второй запрос для проверки следующей. Если подтвердилась · переходи к черновику баг-репорта (5-7 строк).
• Шаг 5 (~5 мин) — финальный вывод: баг-репорт (если баг) или close-as-not-a-bug (если все гипотезы опровергнуты, клиент ошибся).

<strong>Acceptance:</strong>
• Письменный отчёт ~150-200 слов с гипотезами, запросами (полный текст SQL), выводом и финальным действием (черновик баг-репорта или решение закрыть).
• 2-3 гипотезы сформулированы конкретно (не «что-то с email», а «строка email в email_queue для user_id=X не была создана»).
• Запросы воспроизводимы (можно скопировать и запустить в playground).

<strong>Подсказки:</strong>
- Запрос от гипотезы — это дисциплинированный подход. Гипотеза сначала, запрос потом. Не «погляжу, что в таблице» (это исследовательский режим · отдельный).
- Если в playground нет таблицы email_queue — придумай ближайшую (events, notifications, audit_log) и зафиксируй это в рефлексии.
- HW-3 — упрощённая версия Mission T11. HW = тренировка потока на одном сценарии. Mission = полное расследование на 40 минут.

<strong>Self-check вопросы:</strong>
- Какую гипотезу ты подтвердила первой? Это была твоя первая догадка или вторая?
- Что в запросах было бы сложнее, если бы сценарий был «не получили email подтверждения 3 разных клиента за день»?`,
  },
  {
    id: 'hw_s09_01',
    sprintId: 'S09',
    code: 'HW-S09-01',
    title: 'Основы HTTP · 5 запросов',
    icon: '🌐',
    estimateMin: 25,
    priority: 'P1',
    description: 'Закрепление T01-T04 · методы HTTP · коды статуса · форма ответа.',
    prompt: `HTTP-запросы — фундамент любого API-тестирования. Эти 5 запросов закрепляют разницу между методами и статус-кодами. После них ты будешь читать чужие баг-репорты с curl без потерь и писать свои так, чтобы разработчик не переспрашивал.

<strong>Что сделать:</strong>
Закрепи T01-T04 · методы (GET / POST / DELETE) · коды статуса · форму ответа. 5 запросов · для каждого — curl + скриншот ответа Postman + 1-2 строки интерпретации.

<strong>Запросы:</strong>
• 1.1 — GET /api/users/me · ожидаемое: 200 OK + JSON body. Проверь: форму ответа, заголовки (Content-Type), статус-код.
• 1.2 — POST /api/orders с невалидным body (отсутствует обязательное поле) · ожидаемое: 400 или 422 с сообщением об ошибке. Проверь: формат ошибки, какое поле не прошло валидацию.
• 1.3 — DELETE /api/orders/<несуществующий_id> · ожидаемое: 404 или 410. Проверь: статус-код, идемпотентность (повтори · тот же результат).
• 1.4 — GET без заголовка Authorization на защищённый endpoint · ожидаемое: 401. Проверь: ответ описывает, что нужна авторизация.
• 1.5 — POST /api/orders с валидным body · ожидаемое: 201 Created + заголовок Location. Проверь: статус, Location указывает на новый заказ, body содержит id.

<strong>Acceptance:</strong>
• Для каждого пункта — команда curl + скриншот ответа Postman + 1-2 строки интерпретации.
• Коды статуса соответствуют спецификации (200 / 201 / 400 / 401 / 404 / 422).
• Запрос 1.3 — проверка идемпотентности (повторный DELETE даёт тот же статус).

<strong>Подсказки:</strong>
- 400 vs 422: 400 — «запрос сломан» (битый JSON, нет обязательного поля), 422 — «формат валиден, не прошла бизнес-валидация» (price < 0, email уже занят). Разные API трактуют по-разному · зафиксируй, какой использует Atelier.
- 404 vs 410: 404 — «не найден» (может быть «ещё не существовал» или «удалён давно»), 410 — «существовал, удалён окончательно» (явное уведомление об удалении). Большинство API используют 404 для обоих случаев.
- 201 vs 200 для POST: 201 = «создан новый ресурс» (должен включать заголовок Location), 200 = «запрос OK, новый ресурс не создан» (например, POST /api/login).

<strong>Self-check вопросы:</strong>
- Какой body ответа Atelier даёт на 422? Содержит ли список конкретных полей?
- Запрос 1.4 · что произошло бы с невалидным token vs истёкшим token? Один и тот же 401 или разные коды?`,
  },
  {
    id: 'hw_s09_02',
    sprintId: 'S09',
    code: 'HW-S09-02',
    title: 'Postman collection · авторизация + Copy as cURL',
    icon: '🔑',
    estimateMin: 30,
    priority: 'P1',
    description: 'Освоить базовый процесс Postman · collections · environments · variables · Copy as cURL для шеринга.',
    prompt: `Postman — основной инструмент API-тестировщика. После этого упражнения у тебя будет рабочая коллекция с авторизацией, которой ты сможешь делиться через curl без боли с подстановкой токенов.

<strong>Что сделать:</strong>
Создай Postman collection · environment с переменными · цепочку авторизации · Copy as cURL · модификацию для негативного теста.

<strong>Шаги:</strong>
• Шаг 1 (~10 мин) — создай Postman collection «Atelier API · S09 HW». Environment «atelier-dev» с переменными baseUrl, authToken. Сохранённый запрос 1: POST /api/auth/login · ответ сохраняет токен в переменную. Сохранённый запрос 2: GET /api/users/me с {{authToken}} в заголовке Authorization.
• Шаг 2 (~10 мин) — воспроизведи запрос через Copy as cURL. Правый клик → Code → cURL. Скрытие персональных данных: замени реальный токен на плейсхолдер. Вставь в текстовый файл как блок кода.
• Шаг 3 (~10 мин) — модифицируй запрос для негативного теста. Поменяй auth на невалидный токен. Проверь 401 Unauthorized. Зафиксируй в заметках.

<strong>Acceptance:</strong>
• Скриншот Postman collection с environments + variables + сохранённым текстом curl + 1 абзац рефлексии.
• Цепочка токенов работает (login → /users/me использует token автоматически через {{authToken}}).
• Негативный тест (невалидный токен) → 401 проверен · скриншот.
• Copy as cURL — токен заменён плейсхолдером (например \`<REDACTED_TOKEN>\`) — НЕ оставлять реальный токен в shareable тексте.

<strong>Подсказки:</strong>
- Цепочка токенов: во вкладке Tests login-запроса — \`pm.environment.set("authToken", pm.response.json().token);\`. Это стандартный паттерн.
- {{variable}} — синтаксис плейсхолдеров Postman. Работает в URL, headers, body, params — везде.
- Скрытие персональных данных критично перед шерингом curl: реальный токен = доступ ко всему API. Привычка: всегда скрывать в shareable артефактах.

<strong>Self-check вопросы:</strong>
- Что произойдёт, если запустить /users/me ДО login? Postman даст ошибку или попытается с пустым {{authToken}}?
- Какие ещё персональные данные ты бы скрыла в curl кроме токена (email, телефон, адрес)?`,
  },
  {
    id: 'hw_s09_03',
    sprintId: 'S09',
    code: 'HW-S09-03',
    title: 'Мини-расследование API-бага + баг-репорт',
    icon: '🐛',
    estimateMin: 35,
    priority: 'P2',
    description: 'Применить 5-шаговый процесс T07 + формат 11-полевого баг-репорта T08 на меньшем масштабе.',
    prompt: `Расследование API-бага — это «не верь одному ответу, проверь в БД». Здесь упражнение в дисциплине: гипотеза, HAR, воспроизведение, проверка в SQL, баг-репорт. После этого ты будешь оформлять API-баги так, что разработчик сможет починить с первого захода.

<strong>Что сделать:</strong>
Примени 5-шаговый процесс расследования T07 + формат 11-полевого баг-репорта T08 на меньшем масштабе.

<strong>Сценарий:</strong>
В Atelier API есть известный баг в одном endpoint (например, GET /api/cashback возвращает устаревший баланс).

<strong>Шаги:</strong>
• Шаг 1 (~5 мин) — гипотеза (что подозреваешь · 2-3 возможных корневых причины).
• Шаг 2 (~10 мин) — DevTools Network · сохрани HAR · найди подозрительный запрос.
• Шаг 3 (~10 мин) — воспроизведи в Postman · Copy as cURL · запусти · подтверди always vs sometimes.
• Шаг 4 (~5 мин) — проверка в SQL (обязательно · T07) · запрос проверяет реальное состояние в БД.
• Шаг 5 (~5 мин) — баг-репорт по API (T08 формат 11 полей · curl + статус + body + SQL-evidence).

<strong>Acceptance:</strong>
• Письменный баг-репорт ~150-200 слов · по формуле Title из T08 · содержит curl + SQL-запросы (полный текст SQL · не описание).
• Проверка в SQL обязательна · запрос показывает реальное состояние в БД (не доверяй одному ответу API — это правило T07).
• Гипотезы явные: 2-3 кандидата перечислены ДО расследования, не задним числом.
• Frequency подтверждена Always / Often / Sometimes через повторное воспроизведение (не «иногда»).

<strong>Подсказки:</strong>
- API → проверка в SQL (правило T07): любой критичный flow (платежи / заказы / кэшбэк / auth / admin) — после API call проверить реальное состояние в БД через SQL. Без этого можно поймать stale cache как баг, а реально это норма cache TTL.
- HAR file = HTTP Archive · DevTools Network → правый клик → «Save all as HAR». Включает все запросы / ответы с заголовками — лучший артефакт для расследования.
- HW-3 — упрощённая версия Mission T10. HW = тренировка потока на одном дополнительном баге. Mission = полное интегративное упражнение.

<strong>Self-check вопросы:</strong>
- Какая из 2-3 гипотез подтвердилась? Была ли это твоя первая догадка?
- Если бы SQL-запрос показал баланс в БД, совпадающий с ответом API — какая была бы следующая гипотеза (где stale)?`,
  },
  {
    id: 'hw_s10_01',
    sprintId: 'S10',
    code: 'HW-S10-01',
    title: 'Cashback Postman collection · 5 endpoints + цепочка авторизации',
    icon: '📦',
    estimateMin: 45,
    priority: 'P1',
    description: 'Построить Postman collection с нуля для 5 endpoints · полный сетап (environment · переменные · pm.test · Newman) без подсказок.',
    prompt: `Собрать рабочую коллекцию с авторизацией с нуля — это собес-навык для junior'а. После этого упражнения у тебя будет шаблон, который ты сможешь повторить за 20 минут на любом проекте без подглядывания в туториал.

<strong>Что сделать:</strong>
Построй Postman collection с нуля для 5 cashback endpoints. Цель — прочувствовать полный сетап (collection · environment · переменные · pm.test · цепочка токенов · Newman) без подсказок.

<strong>Шаги:</strong>
• 1. Создай collection «Cashback HW · {твоё имя}» в Personal workspace.
• 2. Создай Environment «Dev» с переменными: \`base_url\`, \`auth_token\` (пусто пока), \`default_user_id\`.
• 3. Создай 5 запросов (можешь использовать https://jsonplaceholder.typicode.com или mock endpoint):
  - POST /auth/login (Body: email + password)
  - GET /v2/users/{{default_user_id}}/cashback
  - GET /v2/users/{{default_user_id}}/orders
  - POST /v2/cashback/redeem (auth: Bearer)
  - GET /v2/cashback/audit?user_id={{default_user_id}}
• 4. Каждый запрос имеет минимум 2 pm.test (статус + одна проверка body со строгими типами).
• 5. Login-запрос сохраняет токен через \`pm.environment.set("auth_token", ...)\`.
• 6. Все остальные запросы используют \`{{auth_token}}\` в Authorization.
• 7. Запусти через Collection Runner — все запросы pass (1 итерация).
• 8. Экспортируй collection как JSON · приложи к сдаче HW.

<strong>Бонусная цель:</strong>
Прогнать Newman и приложить HTML-отчёт htmlextra.

<strong>Acceptance:</strong>
• Collection JSON экспортирована + скриншот Collection Runner с 5/5 pass.
• Все 5 запросов в логической цепочке · нет захардкоженных URL / токенов · всё через {{переменные}}.
• pm.test со строгими проверками (не \`.to.exist\` на nullable-полях · использовать \`.to.be.a('number')\` и т.п.).
• Бонус: Newman run + htmlextra отчёт — если сделано, приложи. Не обязательно.

<strong>Подсказки:</strong>
- Строгая проверка: \`pm.expect(response.balance).to.be.a('number')\` — поймает, если backend вернёт строку «100» вместо числа 100. \`.to.exist\` — слабая, проходит даже если поле null.
- Область переменных: переменные уровня Environment видны во всех запросах collection · хорошо для разделяемого состояния (токен).
- Newman = CLI-раннер для Postman collections — мост к CI/CD. \`newman run cashback.json --reporters htmlextra\`.

<strong>Self-check вопросы:</strong>
- Какая pm.test самая «защитная» — поймает максимум потенциальных регрессий?
- Что произойдёт с твоей collection при смене base_url на production? Какие запросы нужно дополнительно проверить?`,
  },
  {
    id: 'hw_s10_02',
    sprintId: 'S10',
    code: 'HW-S10-02',
    title: 'Cross-tool · повторить критичный запрос в SoapUI',
    icon: '🧪',
    estimateMin: 30,
    priority: 'P2',
    description: 'Перенести 1 критичный запрос из HW-S10-01 в SoapUI · показать владение более чем одним инструментом.',
    prompt: `Логика тестирования — это не Postman-specific. Перенос одного запроса в SoapUI учит видеть инструмент как сменное оборудование. Полезно для собеседований («у нас на проекте SoapUI» — ты не растеряешься).

<strong>Что сделать:</strong>
Перенеси 1 критичный запрос из HW-S10-01 (например GET /cashback) в SoapUI. Цель — владение более чем одним инструментом · понимание, что логика тестирования не привязана к Postman.

<strong>Шаги:</strong>
• 1. Установи SoapUI Open Source (бесплатный).
• 2. Создай REST Project «Cashback Cross-Tool».
• 3. Project Properties: те же, что в Postman environment (\`base_url\` и т.д.).
• 4. Создай TestSuite «Sanity» → TestCase «Repeat critical request».
• 5. TestStep 1: REST Request — повтори GET /cashback из HW-S10-01.
• 6. Минимум 3 проверки через UI: Valid HTTP Status (200) · JSONPath Match ($.balance is number) · Response SLA (1000ms).
• 7. TestStep 2: Groovy Script с одним \`log.info\` для отладки наблюдаемого значения.
• 8. Запусти TestCase в SoapUI · скриншот результата.
• 9. Сравни результат Postman vs SoapUI для того же endpoint.

<strong>Acceptance:</strong>
• Скриншот SoapUI TestCase с 3 проверками all pass.
• Сравнение бок о бок: скриншоты Postman vs SoapUI для того же endpoint — ответ идентичен.
• Если различаются — расследуй (опечатка в auth token? не тот env? опечатка в URL?) и зафиксируй в заметках.
• 1 абзац рефлексии: что в SoapUI было проще / труднее / отличается от Postman.

<strong>Подсказки:</strong>
- Проверки в SoapUI через UI — это GUI-эквивалент pm.test из Postman. Под капотом — те же проверки JsonPath / Regex / XPath.
- Groovy-шаг не обязателен для базовых flow, но полезен для условной логики или подготовки данных.
- Проверка Response SLA (1000ms) — встроенная performance-проверка в SoapUI. В Postman аналог — pm.test через \`pm.response.responseTime\`.

<strong>Self-check вопросы:</strong>
- Какие пункты сетапа Postman отсутствуют в SoapUI (или наоборот)? Это минус или плюс?
- Для команды из 5 QA — какой инструмент ты бы выбрала и почему?`,
  },
  {
    id: 'hw_s10_03',
    sprintId: 'S10',
    code: 'HW-S10-03',
    title: 'AI-ревью тестов · 6 шагов цикла ревью',
    icon: '🤖',
    estimateMin: 30,
    priority: 'P2',
    description: 'Применить AI-процесс (T09) к реальному коду тестов · натренировать привычку критичного ревью AI-предложений.',
    prompt: `AI хорошо генерирует тесты, но без ревью junior рискует адоптить hallucination. Здесь упражнение в критичном чтении: что взять, что отбросить и почему. После этого ты будешь использовать AI как «второе мнение», а не «решение».

<strong>Что сделать:</strong>
Примени AI-workflow (T09) к реальному pm.test-коду из HW-S10-01. Цель — натренировать критичное ревью AI-предложений, не «всё что AI сказал = берём».

<strong>Шаги:</strong>
• 1. Возьми collection из HW-S10-01.
• 2. Скопируй pm.test-код одного запроса в AI-чат (ChatGPT / Claude / Postman Postbot).
• 3. Промпт: «Проревьюй мои pm.test-скрипты. Endpoint: [твой endpoint]. Expected: [форма ответа]. Что я упустила? Какие крайние случаи?»
• 4. Получи фидбек от AI (минимум 3 предложения).
• 5. Применить чеклист ревью к предложениям AI:
  - URL endpoint и методы корректны?
  - Имена полей совпадают с твоим реальным API?
  - Логика проверок не даёт false-pass?
  - Expected values совпадают с реальным API?
  - Крайний случай релевантен (для твоего контекста)?
• 6. Возьми 1-2 лучших предложения AI · обнови pm.test-код.
• 7. Отбрось 1-2 предложения AI с обоснованием «не релевантно, потому что...».
• 8. Запиши сводку: что AI предложил · что взяла · что отбросила · почему.

<strong>Acceptance:</strong>
• Сводка в markdown: список предложений AI (минимум 3) · вердикт по каждому (взять / отбросить) · обоснование решения.
• Не все «взять» и не все «отбросить» — сбалансированное чтение (правило T09).
• В сводке видно критичное мышление · хотя бы одно предложение AI поймано на hallucination (придумал поле или endpoint, которого нет).
• Обновлённый pm.test-код приложен (после интеграции принятых предложений).

<strong>Подсказки:</strong>
- Hallucinations AI типичны: придумывает несуществующие поля в ответе, выдумывает URL-пути. Чеклист ревью (пункты 1-4) ловит это.
- «Не всё взять, не всё отбросить» — это не квота. Если все 3 предложения реально хорошие — возьми все 3 и зафиксируй «AI дал 3/3 валидных предложения на этот сценарий».
- AI хорошо генерирует крайние случаи (негативные тесты, граничные значения) — это часто слепое пятно junior'ов.

<strong>Self-check вопросы:</strong>
- Какое предложение AI ты бы отбросила, даже не дойдя до чеклиста? Почему сразу очевидно плохо?
- Если бы AI предложил 10 крайних случаев — сколько ты бы взяла? Какой trade-off покрытия и поддержки?`,
  },
  {
    id: 'hw_s11_01',
    sprintId: 'S11',
    code: 'HW-S11-01',
    title: 'Глубокий SQL · 5 запросов (подзапрос / CTE / window / LAG)',
    icon: '🔬',
    estimateMin: 40,
    priority: 'P1',
    description: 'Написать 5 запросов с подзапросами / CTE / window functions для реальных бизнес-вопросов.',
    prompt: `Базовые SELECT/JOIN/GROUP BY покрывают 70% работы, но senior-вопросы аналитики требуют подзапросов, CTE и window functions. После этих 5 запросов ты сможешь отвечать на «сколько каждый юзер потратил с течением времени» без зависания.

<strong>Что сделать:</strong>
Напиши 5 продвинутых SQL-запросов · примени подзапросы · цепочки CTE · рекурсивные CTE · window functions · LAG. Цель — практика advanced SQL для реальных бизнес-вопросов.

<strong>Запросы:</strong>
• 1. Подзапрос — найти заказы с суммой больше средней по своему месяцу (correlated).
• 2. Цепочка CTE — топ-3 категории по revenue в каждой стране за последние 30 дней (3 связанных CTE + window function).
• 3. Рекурсивный CTE — обойти дерево категорий от корня · посчитать суммарное число товаров в каждом поддереве.
• 4. Window function — для каждого пользователя — нарастающий итог revenue по времени (SUM OVER ORDER BY).
• 5. LAG для поиска разрывов — найти пользователей, где разрыв между 2 последними заказами > 60 дней.

<strong>Acceptance:</strong>
• Все 5 запросов запускаются на demo БД · возвращают разумные результаты.
• Код читабельный (CTE для многошаговой логики) · отступы согласованы.
• Рекурсивный CTE имеет защитный лимит \`WHERE depth < 100\` (правило T03).
• Window function использует \`PARTITION BY\` + \`ORDER BY\` (не просто \`OVER ()\`).

<strong>Подсказки:</strong>
- Correlated subquery — медленнее non-correlated (выполняется построчно), но иногда единственный способ. Для «больше среднего по месяцу» — correlated по полю месяца.
- Рекурсивный CTE: \`WITH RECURSIVE tree AS (якорь SELECT UNION ALL рекурсия SELECT)\`. Якорь = корень, рекурсия = self-reference. Без защитного лимита — риск бесконечного цикла.
- LAG(field, 1) OVER (PARTITION BY user_id ORDER BY date) — предыдущая строка в той же группе. Разница current - LAG = разрыв во времени.

<strong>Self-check вопросы:</strong>
- Какой из 5 запросов был сложнее всего написать? Что мешало — концепция или синтаксис?
- Рекурсивный CTE — где ещё в реальной работе QA можно применить (orgchart, файловая система, ветки комментариев)?`,
  },
  {
    id: 'hw_s11_02',
    sprintId: 'S11',
    code: 'HW-S11-02',
    title: 'MongoDB + Redis на практике · 5 операций',
    icon: '🗄',
    estimateMin: 30,
    priority: 'P2',
    description: 'Освоить базовые операции в обоих NoSQL-инструментах · сравнить с SQL.',
    prompt: `Реальные продукты комбинируют PostgreSQL с NoSQL: Mongo для гибкой схемы документов, Redis для кэша и очередей. Эти 5 операций дают первое прикосновение, чтобы на собеседовании не растеряться от вопроса «работала с Mongo?».

<strong>Что сделать:</strong>
Освой базовые операции в MongoDB + Redis. Сравнение с SQL.

<strong>Шаги:</strong>
• 1. MongoDB: установи локально или Atlas free · создай коллекцию products · вставь 10 документов · напиши 3 запроса (find + projection · aggregation $group · find с вложенным фильтром).
• 2. Redis: установи локально · попрактикуй 4 use case'а (string GET/SET · hash HGETALL · list LPUSH/RPOP queue · sorted set leaderboard ZADD/ZRANGE).
• 3. Сравнение: напиши 1 запрос, который делает то же в Mongo и SQL · сравни синтаксис · какой читабельнее для junior'а.

<strong>Acceptance:</strong>
• Скриншоты или текстовый вывод 5 запусков запросов / команд (3 Mongo + 4 Redis с группировкой по типу данных).
• Сравнение · 1 абзац наблюдения о различиях (синтаксис · выразительность · читабельность для junior'а).
• Если локальная установка не получается — Atlas free / Redis Cloud free / docker — зафиксируй в заметках, какой путь выбрала.

<strong>Подсказки:</strong>
- MongoDB find + projection: \`db.products.find({category: "X"}, {name: 1, price: 1, _id: 0})\` — _id всегда возвращается, если не отключить.
- Redis use case'ы: string = кэш (рендер страницы), hash = профиль пользователя, list = очередь задач, sorted set = leaderboard. У каждого паттерна есть «обычное» применение.
- Сравнение: SQL \`SELECT name, price FROM products WHERE category='X'\` vs Mongo \`db.products.find({category:'X'}, {name:1, price:1, _id:0})\`. SQL более декларативный, Mongo более JSON-shape.

<strong>Self-check вопросы:</strong>
- Какой синтаксис интуитивнее для junior'а — SQL или Mongo? Почему?
- Для какого use case'а Redis явно лучше PostgreSQL (latency-критичный · простой key-value)?`,
  },
  {
    id: 'hw_s11_03',
    sprintId: 'S11',
    code: 'HW-S11-03',
    title: 'Faker seed-скрипт с защитой от случайного DML',
    icon: '🎲',
    estimateMin: 35,
    priority: 'P3',
    description: 'Написать production-уровня seed-скрипт · применить паттерны S08 + S11 · защита от запуска против production.',
    prompt: `Скрипт для наполнения тестовой БД — типичный артефакт QA. Главная ловушка — случайно запустить против production. Здесь упражнение в защитных привычках: ENV-guard, транзакции, fail-fast. После этого ты будешь писать скрипты, которые не сломают продакшен.

<strong>Что сделать:</strong>
Напиши production-уровня Python seed-скрипт с Faker · multi-locale · защитой от случайного DML · fail-fast guard'ами.

<strong>Шаги:</strong>
• 1. Python-скрипт с Faker · multi-locale (минимум 4 локали).
• 2. Создай 30 users · 60 products · 100 orders · 200 order_items с корректными FK-связями.
• 3. DML-обёртка (BEGIN → цикл → проверка количества → COMMIT/ROLLBACK).
• 4. Fail-fast assert на ENVIRONMENT (только dev/staging).
• 5. Логирование: вывод сводки («Создано 30 users · 60 products · 100 orders · 200 order_items»).

<strong>Acceptance:</strong>
• Прогон против локального PostgreSQL · проверка количества через SELECT (счёт совпадает со сводкой скрипта).
• DML-обёртка обязательна: BEGIN перед циклом · COMMIT после проверки · ROLLBACK если проверка не прошла.
• Fail-fast assert на os.environ['ENV'] · сразу exit если 'production' · приложи скриншот демонстрации отказа.
• Логи чистые (timestamp · счётчики · pass/fail · ошибки видны).

<strong>Подсказки:</strong>
- Faker locale: \`Faker(['en_US', 'ru_RU', 'ja_JP', 'ar_SA'])\` — для каждого user fake-данные из случайной локали. Покрытие Unicode автоматическое.
- Порядок FK важен: users → products → orders (user_id FK) → order_items (order_id + product_id FK). Цикл в этом порядке, иначе нарушение FK constraint.
- ENV-guard: \`assert os.environ.get('ENV') in ('dev', 'staging'), f"Refusing to seed in {os.environ.get('ENV')}"\`. Это первая строка после imports.

<strong>Self-check вопросы:</strong>
- Что произошло бы без ENV-guard, если случайно запустить против production credentials?
- ROLLBACK trigger — какая проверка может его вызвать? (Например, COUNT(orders) ≠ ожидаемому.)`,
  },
  {
    id: 'hw_s12_01',
    sprintId: 'S12',
    code: 'HW-S12-01',
    title: 'Non-functional аудит публичной страницы · сводный отчёт',
    icon: '🔬',
    estimateMin: 45,
    priority: 'P1',
    description: 'Провести non-functional аудит одной публичной web-страницы (e-commerce / новостной портал · НЕ корпоративный production · НЕ персональные данные).',
    prompt: `Non-functional аудит — это сильный артефакт junior'а на собесе: «вот за час я нашла 5 findings на публичном сайте». Здесь упражнение в комбинации 4 инструментов (Lighthouse + axe + клавиатура + контраст) и приоритизации находок.

<strong>Что сделать:</strong>
Сводный non-functional аудит одной публичной web-страницы (любой e-commerce / новостной / SaaS landing) по 6 направлениям · приоритизированный отчёт top-5 находок.

<strong>Важно про границы:</strong>
- НЕ корпоративный production · НЕ персональные данные · НЕ за пределами terms of service сайта.
- Любой публичный e-commerce / новостной / SaaS landing — допустимо.
- Для security-проверки (5) — только если сайт авторизует тестирование ИЛИ это твой личный sandbox.

<strong>Шаги:</strong>
• 1. Lighthouse audit · сохрани score + Web Vitals + top-3 opportunities · сохрани HTML-отчёт.
• 2. axe DevTools скан · сохрани список violations · скриншот.
• 3. Ручная навигация с клавиатуры · отметь проблемы (минимум 5 или «не нашла»).
• 4. Проверка цветового контраста на 5 подозрительных элементах.
• 5. Опционально · если уверена · OWASP-awareness проверка (базовый XSS payload в поле поиска · если сайт авторизует тестирование ИЛИ это твой sandbox).
• 6. Сводный отчёт (1 страница) · top-5 находок приоритизированы.

<strong>Acceptance:</strong>
• Все 4 обязательных аудита сделаны (Lighthouse · axe · клавиатурная навигация · контраст) · evidence приложен (HTML-отчёт · скриншоты).
• Приоритизация явная (top-5 ранжированы по impact + likelihood) · не просто несортированный список.
• У каждой находки явная категория (Performance / Accessibility / Security / Usability / Other).
• 1 строка business impact на находку.

<strong>Подсказки:</strong>
- Lighthouse встроен в Chrome DevTools (вкладка «Lighthouse»). Прогон 30 секунд. Score < 90 — есть что починить.
- axe DevTools — расширение Chrome от Deque · отраслевой стандарт автоматического a11y-тула · ~85% покрытия.
- Клавиатурная навигация: Tab по всем интерактивным элементам. Ловушка (нельзя выйти Tab'ом) — это находка. Skip links отсутствуют — находка.
- OWASP-awareness проверка НЕ означает «найди уязвимости и эксплуатируй». Означает: «есть ли базовая XSS-санитизация, есть ли HTTPS, есть ли CSP headers».

<strong>Self-check вопросы:</strong>
- Какие 2 находки из 5 скорее всего реально починит команда сайта (если бы получила твой отчёт)?
- Какое из 6 направлений дало больше всего находок? Что это говорит о трендах в индустрии?`,
  },
  {
    id: 'hw_s12_02',
    sprintId: 'S12',
    code: 'HW-S12-02',
    title: 'Матрица доступов (authz) для маленького публичного приложения',
    icon: '🔐',
    estimateMin: 30,
    priority: 'P1',
    description: 'Построить матрицу доступов (роли × endpoints) для маленького публичного приложения · ручная проверка 3 ячеек.',
    prompt: `IDOR и broken authz — лидеры OWASP Top 10. После этого упражнения у тебя будет готовая матрица доступов и опыт ручной проверки 3 ячеек — артефакт, который можно показать на собесе как «делала security-проверку публичного приложения».

<strong>Что сделать:</strong>
Построй матрицу доступов (роли × endpoints) для маленького публичного приложения · ручная проверка 3 ячеек.

<strong>Важно про границы:</strong>
- Личный блог · sandbox-проект · open-source приложение · НЕ корпоративный production.
- Если нет такого приложения — используй Atelier sandbox-tracker (создаётся в S07) с гипотетическими ролями.

<strong>Шаги:</strong>
• 1. Определи endpoints (минимум 5) + роли (минимум 3 · например visitor · authenticated user · admin если доступно).
• 2. Построй таблицу (роли × endpoints · ожидаемый ответ в каждой ячейке).
• 3. Ручная проверка минимум 3 ячеек (роль × endpoint) · сравни фактический vs ожидаемый.
• 4. Сдай матрицу + находки (если уязвимости найдены).

<strong>Acceptance:</strong>
• Матрица полная · все ячейки задокументированы (5 endpoints × 3 роли = 15 ячеек · ожидаемый ответ в каждой).
• Минимум 3 ячейки проверены вручную · evidence скриншотами · сравнение фактический vs ожидаемый.
• Крайние случаи отмечены (например «authenticated user обращается к своим vs чужим данным»).
• Если уязвимость найдена — черновик баг-репорта (severity · impact · шаги POC · предлагаемый fix).

<strong>Подсказки:</strong>
- Ожидаемые ответы: 200 OK (разрешено) · 401 Unauthorized (нет auth) · 403 Forbidden (auth OK, нет прав) · 404 Not Found (существование скрыто от неавторизованного). 404 vs 403 — это сознательный design (404 скрывает существование, 403 подтверждает).
- Паттерн IDOR: меняешь параметр в URL (user_id, order_id) для доступа к чужому ресурсу. Очень частая уязвимость.
- Horizontal vs vertical: horizontal = та же роль, другой пользователь (user A видит данные user B). Vertical = повышение роли (user видит admin-страницы).

<strong>Self-check вопросы:</strong>
- Какие из 5 endpoints вероятнее всего имеют сломанную authz (IDOR)? Почему именно они?
- Если добавить 4-ю роль (например \`moderator\`) — сколько новых ячеек добавится в матрицу?`,
  },
  {
    id: 'hw_s12_03',
    sprintId: 'S12',
    code: 'HW-S12-03',
    title: 'AI-промпты в 2 секциях · 6 промптов с циклом ревью',
    icon: '🤖',
    estimateMin: 30,
    priority: 'P2',
    description: 'Освоить AI-workflow Секция A (генерация идей тестов) + Секция B (интерпретация результатов) · по 3 промпта на секцию.',
    prompt: `AI в QA полезен в двух режимах: генерация (тестовых идей, payloads, матриц) и интерпретация (Lighthouse-отчётов, waterfall-анализа). Здесь упражнение в обоих режимах с критичным ревью каждого ответа. После этого ты будешь чётко понимать, где AI ускоряет, а где галлюцинирует.

<strong>Что сделать:</strong>
Освой AI-workflow Секция A (генерация идей тестов) + Секция B (интерпретация результатов). По 3 промпта на секцию · применён чеклист ревью.

<strong>Шаги:</strong>
• 1. Секция A — генерация: 3 промпта (XSS payloads · скелет матрицы доступов · нарушения API-контракта) · сохрани ответы.
• 2. Секция B — интерпретация: 3 промпта (интерпретация Lighthouse · анализ waterfall · рекомендации по оптимизации) · сохрани ответы.
• 3. Примени чеклист ревью · отметь 1-2 hallucination / проблемы / сильные стороны на каждый ответ.
• 4. Соберись сводку: «наблюдаемые сильные стороны AI» · «пробелы AI» · «рекомендации по применению».

<strong>Acceptance:</strong>
• 6 промптов сделаны (3 Секция A + 3 Секция B) · ответы сохранены · заметки ревью на каждый.
• Хотя бы 1 hallucination пойман и зафиксирован (AI выдумал endpoint / поле / уязвимость, которой нет).
• Хотя бы 1 сильная сторона отмечена (AI генерирует / интерпретирует быстрее / шире / свежее, чем junior сам).
• Сводка сбалансирована · не «AI всё правильно» · не «AI всё неправильно» · конкретные наблюдения.

<strong>Подсказки:</strong>
- Секция A (генерация) — AI хорош на тривиальном фаззинге (XSS payloads, SQL-инъекции, матрицы доступов). Слабее на domain-specific тонких случаях.
- Секция B (интерпретация) — AI часто overconfident (трактует waterfall корректно, но без знания контекста твоего продукта). Полезен как «второе мнение», не «решение».
- Рекомендации по применению: где конкретно в твоём процессе AI помогает (генерация тестовых идей? триаж багов? черновик отчёта?) vs где НЕ помогает (production-aware решения · новая предметная область).

<strong>Self-check вопросы:</strong>
- Какой промпт дал самый полезный ответ? Почему — точная формулировка, конкретный контекст, или AI просто угадал?
- В какой ситуации ты бы отказалась от AI и сделала вручную? Почему?`,
  },
];

export const homeworkBySprintId = (sprintId: string): Homework[] =>
  HOMEWORK.filter((h) => h.sprintId === sprintId);
